import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Analytics01FreeIcons, MapsFreeIcons, FavouriteFreeIcons, UserFreeIcons, Calendar01FreeIcons, Location01FreeIcons, FilterFreeIcons, FireFreeIcons, GpsFreeIcons, Sun02FreeIcons, Moon02FreeIcons } from '@hugeicons/core-free-icons';
import { FavouriteIcon, BalanceScaleIcon, User02Icon, Search01Icon, Location01Icon, MapsIcon, Megaphone02Icon, Notification01Icon, Share01Icon, Clock01Icon, Calendar01Icon, Building01Icon, AddCircleIcon, CustomerSupportIcon, Mail01Icon } from 'hugeicons-react';

// Mahalle verisi module-level cache (bir kere yüklenir)
let _mahalleData = null;
const getMahalleData = () => {
  if (_mahalleData) return Promise.resolve(_mahalleData);
  return fetch('/data/mahalleler.json')
    .then(r => r.json())
    .then(d => { _mahalleData = d; return d; });
};
import * as Sentry from '@sentry/react';
import { supabase } from './lib/supabase';
import { TURKEY_CITIES } from './lib/turkey';
import { CATS, TC, MODES, NOTIFICATIONS, BADGES, BADGE_CAT_LABELS } from './data/constants';
import { formatPhone, capitalizeWords, fmtD } from './utils/format';
import { parseSeoPath, applySeoMeta } from './utils/seoLanding';
import { hav, makeCircle } from './utils/geo';

// HTML escape — Mapbox popup setHTML / innerHTML injection guard
const escapeHTML = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
import { haptic } from './utils/haptic';
import { matchScore, scoreColor, loadPrefs } from './utils/matching';
import { PreferencesModal } from './components/PreferencesModal';
import { Maskot } from './components/Maskot';
import { AddSchoolModal } from './components/AddSchoolModal';
import { SupportFormModal } from './components/SupportFormModal';
import { BilgiAlModal } from './components/BilgiAlModal';
import { ReviewForm } from './components/ReviewForm';
import { AlertModal } from './components/AlertModal';
import { ClaimModal } from './components/ClaimModal';
import { DetailModal } from './components/DetailModal';
import { AuthModal } from './components/AuthModal';
import { InstitutionProfileModal } from './components/InstitutionProfileModal';
import { CompareModal } from './components/CompareModal';
import { Filters } from './components/Filters';
import { MobileTopBar, MobileCategoryBar, MobileFilterSheet, LocationPermissionModal, LocationOnboardingModal, MobileLocationSheet, MobileBottomNav } from './components/MobileComponents';

// Mapbox public token — env var öncelikli, fallback hardcoded (token rotate edilirse env'den güncelle)
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoib2t1bGJvdCIsImEiOiJjbW9meTR3c3MwanplMnFzYmxpbWRqem94In0.BgLh_eMKT9K0zurih6wiwA';

// Mesafe biçimlendirme yardımcısı — m / km
const fmtDist = (m) => m < 1000 ? Math.round(m) + ' m' : (m / 1000).toFixed(1) + ' km';

// Bir sonraki iş gününün 08:00'ı için ISO timestamp — Mapbox depart_at için
const nextWeekday8am = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return d.toISOString().slice(0, 19) + 'Z';
};

// Yürüyüş + arabayla (sabah trafiği) rotaları paralel çek, yürüyüşü haritada çiz
const drawWalkRoute = async (map, uLng, uLat, sLng, sLat) => {
  const departAt = nextWeekday8am();
  const walkUrl = `https://api.mapbox.com/directions/v5/mapbox/walking/${uLng},${uLat};${sLng},${sLat}?geometries=geojson&access_token=${MAPBOX_TOKEN}`;
  const driveUrl = `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${uLng},${uLat};${sLng},${sLat}?geometries=geojson&depart_at=${encodeURIComponent(departAt)}&access_token=${MAPBOX_TOKEN}`;
  const [walkRes, driveRes] = await Promise.all([
    fetch(walkUrl).then(r => r.json()).catch(() => null),
    fetch(driveUrl).then(r => r.json()).catch(() => null),
  ]);
  const walk = walkRes?.routes?.[0];
  const drive = driveRes?.routes?.[0];
  if (!walk && !drive) return null;
  if (walk) {
    try { if(map.getLayer('walk-route-lyr')) map.removeLayer('walk-route-lyr'); } catch(e){}
    try { if(map.getSource('walk-route-src')) map.removeSource('walk-route-src'); } catch(e){}
    map.addSource('walk-route-src', { type:'geojson', data:{ type:'Feature', geometry: walk.geometry } });
    map.addLayer({ id:'walk-route-lyr', type:'line', source:'walk-route-src',
      paint:{ 'line-color':'#A8DAFF', 'line-width':4, 'line-opacity':0.85, 'line-dasharray':[1,0] } });
  }
  return {
    duration: walk ? Math.round(walk.duration / 60) : null,
    distance: walk ? fmtDist(walk.distance) : null,
    driveDuration: drive ? Math.round(drive.duration / 60) : null,
    driveDistance: drive ? fmtDist(drive.distance) : null,
  };
};

const clearWalkRouteFromMap = (map) => {
  try { if(map.getLayer('walk-route-lyr')) map.removeLayer('walk-route-lyr'); } catch(e){}
  try { if(map.getSource('walk-route-src')) map.removeSource('walk-route-src'); } catch(e){}
};


// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function OkulMap() {
  const [theme, setTheme] = useState('dark');
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [staffInfo, setStaffInfo] = useState(null); // { role, url } — staff'ın panele girebileceği bilgi
  const [staffBannerDismissed, setStaffBannerDismissed] = useState(() => {
    try { return localStorage.getItem('okulmap_staff_banner_dismissed') === '1'; } catch { return false; }
  });
  const [favs, setFavs] = useState([]);
  const [search, setSearch] = useState('');
  // PERF: liste paneli pagination — 1300 kart yerine 50 + "daha fazla"
  const [listLimit, setListLimit] = useState(50);
  // Filter/search değişince listLimit'i sıfırla (otomatik scroll resetlenir)
  const [mode, setMode] = useState('normal');
  const [userPos, setUserPos] = useState(null);
  const [locLabel, setLocLabel] = useState('Kadıköy, İstanbul');
  const [selectedId, setSelectedId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const detailEverOpenedRef = useRef(false);
  const [instProfileId, setInstProfileId] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authShowEmail, setAuthShowEmail] = useState(false);
  const [sortBy, setSortBy] = useState('distance');
  const [activeCategory, setActiveCategory] = useState('all');
  const [routeId, setRouteId] = useState(null);
  const [showBilgiAl, setShowBilgiAl] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [notifications, setNotifications] = useState(NOTIFICATIONS);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const [mobileTab, setMobileTab] = useState('map');
  const [calendarFilter, setCalendarFilter] = useState('all');
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarSelectedDay, setCalendarSelectedDay] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedIl, setSelectedIl] = useState('');
  const [selectedIlce, setSelectedIlce] = useState('');
  const [ilceBbox, setIlceBbox] = useState(null); // [minLng, minLat, maxLng, maxLat]
  const [campaigns, setCampaigns] = useState([]);
  const [childAge, setChildAge] = useState(4);
  const [walkRoute, setWalkRoute] = useState(null); // {schoolId, duration, distance}
  const [walkTarget, setWalkTarget] = useState(null); // {lng, lat}
  const campaignsBySchool = useMemo(() => {
    const m = {};
    campaigns.forEach(c => { if (!m[c.school_id]) m[c.school_id] = c; });
    return m;
  }, [campaigns]);
  const [leftPanelView, setLeftPanelView] = useState('list');
  const [mapLoaded, setMapLoaded] = useState(false);
  // setStyle (tema değişimi) tüm custom layer'ları siler. styleVersion bump
  // edilince province useEffect re-trigger olur, choropleth yeniden eklenir.
  const [styleVersion, setStyleVersion] = useState(0);
  const [selectedMahalleler, setSelectedMahalleler] = useState([]); // [{name, center:[lng,lat]}]
  const [mahalleQuery, setMahalleQuery] = useState('');
  const [mahalleResults, setMahalleResults] = useState([]);
  const [mahalleSearching, setMahalleSearching] = useState(false);
  const [mahallePanelOpen, setMahallePanelOpen] = useState(false);
  const [showAllCats, setShowAllCats] = useState(false);
  const catBarRef = useRef(null);
  const [ilDropdownOpen, setIlDropdownOpen] = useState(false);
  const [ilceDropdownOpen, setIlceDropdownOpen] = useState(false);
  const [ilQuery, setIlQuery] = useState('');
  const [ilceQuery, setIlceQuery] = useState('');
  const [priceRange, setPriceRange] = useState([0, 6000]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
  const [toast, setToast] = useState(null);
  const [listView, setListView] = useState('list'); // 'list' | 'grid'
  const [recentIds, setRecentIds] = useState(() => { try { return JSON.parse(localStorage.getItem('okulmap_recent')||'[]'); } catch(e){ return []; } }); // max 10
  const [userPrefs, setUserPrefs] = useState(() => loadPrefs());
  const [showPrefsModal, setShowPrefsModal] = useState(false);
  const [radiusKm, setRadiusKm] = useState(null); // null = kapalı
  // Onboarding 3-adımlı pencere kaldırıldı — mobilde performansı kasıyordu.
  // State korunuyor (map init effect'inde kullanılıyor) ama her zaman false.
  const [showOnboarding] = useState(false);
  const [allSchoolBadges, setAllSchoolBadges] = useState({}); // {schoolId: [{key,verified}]}
  const [schoolBadges, setSchoolBadges] = useState([]); // current detail school badges
  const [showAlertModal, setShowAlertModal] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(null);
  const [showPwaPrompt, setShowPwaPrompt] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [eventsData, setEventsData] = useState([]);
  const eventMarkersRef = useRef({});
  const [eventReserveModal, setEventReserveModal] = useState(null);
  const [reserveForm, setReserveForm] = useState({name:'',phone:'',guest_count:'1',note:''});
  const [reserveSaving, setReserveSaving] = useState(false);
  const [reserveMsg, setReserveMsg] = useState(null);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [showMobileLocation, setShowMobileLocation] = useState(false);
  // İlk girişte konum modalı otomatik açılmıyor — kullanıcı kasanmasın.
  // Manuel olarak (konum butonu vs.) tetiklendiğinde açılabilir.
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showLocPermModal, setShowLocPermModal] = useState(false);
  const [locModalStep, setLocModalStep] = useState('choose');
  const [locModalGpsError, setLocModalGpsError] = useState('');
  const [locModalIlQuery, setLocModalIlQuery] = useState('');
  const [locModalIl, setLocModalIl] = useState('');
  const [locModalIlce, setLocModalIlce] = useState('');

  const pwaPromptRef = useRef(null);

  const mapRef = useRef(null);
  const placesRef = useRef([]);
  const mapContainerRef = useRef(null);
  const markersRef = useRef({});
  // Drop animasyonu PIN BAŞINA tek seferlik. Set bu session'da drop
  // edilen okul id'lerini tutar. Yeni viewport'a giren okul ilk kez
  // görünüyorsa düşer, daha önce drop edildiyse anında çıkar.
  // Sayfa refresh'inde Set sıfırlanır → hepsi yeniden düşer.
  const droppedPinsRef = useRef(new Set());
  // Tek bir popup açık tut — yeni pin'e basınca eskisi kapanır
  const activePopupRef = useRef(null);
  const closeActivePopup = () => {
    const p = activePopupRef.current;
    if (p) {
      try { if (p.isOpen()) p.remove(); } catch {}
      activePopupRef.current = null;
    }
  };
  const applyZoomRef = useRef(null);
  const clusterHidingRef = useRef(false);
  const lastSourceKeyRef = useRef('');
  const modeRef = useRef(mode);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  const userPosRef = useRef(null);
  const ilceCenterRef = useRef(null);
  const ilBtnRef = useRef(null);
  const ilceBtnRef = useRef(null);
  const userMarkerRef = useRef(null);
  const priceSliderRef = useRef(null);
  const priceSliderDrag = useRef(null); // 'min' | 'max'
  const zoneSliderRef = useRef(null);

  const isDark = theme === 'dark';
  const C = useCallback((k) => {
    const d = theme === 'dark';
    const m = {
      bg:d?'#0d1117':'#FFF8F0',
      surf:d?'#141924':'#FFFAF5',
      surf2:d?'rgba(255,255,255,0.03)':'rgba(139,92,246,0.04)',
      card:d?'#141924':'#FFF3E6',
      bord:d?'rgba(255,255,255,0.07)':'rgba(139,92,246,0.10)',
      bord2:d?'rgba(255,255,255,0.12)':'rgba(139,92,246,0.14)',
      txt:d?'#ffffff':'#1E1048',
      txt2:d?'rgba(255,255,255,0.5)':'#4B3D7A',
      txt3:d?'rgba(255,255,255,0.25)':'#9B8FBD',
      inp:d?'rgba(255,255,255,0.05)':'rgba(139,92,246,0.06)',
      nav:d?'rgba(13,17,23,0.96)':'rgba(255,248,240,0.97)',
    };
    return m[k]||'';
  }, [theme]);

  // Keep ref in sync for map callbacks
  userPosRef.current = userPos;

  // ─── ZONE SLIDER HANDLERS (OkulMap seviyesinde — remount olmaz) ─────────────
  const onZoneMouseDown = useCallback((e) => {
    e.preventDefault();
    const getAge = (clientX) => {
      const rect = zoneSliderRef.current.getBoundingClientRect();
      return Math.round(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * 16);
    };
    setChildAge(getAge(e.clientX));
    const onMove = (ev) => setChildAge(getAge(ev.clientX));
    const onUp   = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const onZoneTouchStart = useCallback((e) => {
    const getAge = (clientX) => {
      const rect = zoneSliderRef.current.getBoundingClientRect();
      return Math.round(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * 16);
    };
    setChildAge(getAge(e.touches[0].clientX));
    const onMove = (ev) => setChildAge(getAge(ev.touches[0].clientX));
    const onEnd  = () => { document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onEnd); };
    document.addEventListener('touchmove', onMove, {passive:true});
    document.addEventListener('touchend', onEnd);
  }, []);

  // ─── TOAST ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  // ─── ANALYTICS TRACKING ─────────────────────────────────────────────────────
  const trackAnalytics = useCallback(async (schoolId, eventType, eventData = {}) => {
    console.log('[Analytics] INSERT payload:', {school_id: schoolId, event_type: eventType, ...eventData});
    try { await supabase.from('school_analytics').insert({ school_id: schoolId, event_type: eventType }); } catch(e) {}
    // 'view' event'inde schools.view_count++ — owner dashboard için sayaç (RPC varsa atomik, yoksa fallback)
    if (eventType === 'view') {
      try {
        const { error } = await supabase.rpc('increment_view_count', { school_id_in: schoolId });
        if (error) {
          // RPC yoksa fallback: read+write (race condition ihtimali var ama kabul edilebilir)
          const { data } = await supabase.from('schools').select('view_count').eq('id', schoolId).maybeSingle();
          await supabase.from('schools').update({ view_count: (data?.view_count || 0) + 1 }).eq('id', schoolId);
        }
      } catch(e) {}
    }
    if (typeof window.gtag === 'function') window.gtag('event', eventType, { school_id: String(schoolId), ...eventData });
  }, []);

  // ─── WALK ROUTE HANDLERS ────────────────────────────────────────────────────
  const handleWalkRoute = useCallback((school) => {
    const map = mapRef.current;
    if (!map) return;
    const draw = async (lng, lat) => {
      try {
        const info = await drawWalkRoute(map, lng, lat, school.lng, school.lat);
        if (info) { setWalkRoute({ schoolId: school.id, ...info }); setWalkTarget({ lng: school.lng, lat: school.lat }); }
        else showToast('Rota bulunamadı.');
      } catch(e) { showToast('Rota çizilirken hata oluştu.'); }
    };
    const prev = userPosRef.current;
    if (prev) {
      const lng = Array.isArray(prev) ? prev[0] : prev.lng;
      const lat = Array.isArray(prev) ? prev[1] : prev.lat;
      draw(lng, lat);
      return;
    }
    if (!navigator.geolocation) { showToast('Tarayıcınız konum desteklemiyor.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => draw(pos.coords.longitude, pos.coords.latitude),
      () => showToast('Konum alınamadı. Lütfen konum iznine izin verin.'),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  const handleClearWalkRoute = useCallback(() => {
    setWalkRoute(null);
    setWalkTarget(null);
    const map = mapRef.current;
    if (map) clearWalkRouteFromMap(map);
  }, []);

  // ─── WALK ARRIVAL DETECTION ──────────────────────────────────────────────────
  useEffect(() => {
    if (!userPos || !walkTarget) return;
    const uLng = Array.isArray(userPos) ? userPos[0] : userPos.lng;
    const uLat = Array.isArray(userPos) ? userPos[1] : userPos.lat;
    const dist = Math.sqrt(
      Math.pow(uLng - walkTarget.lng, 2) +
      Math.pow(uLat - walkTarget.lat, 2)
    ) * 111000;
    if (dist < 50) {
      setWalkRoute(null);
      setWalkTarget(null);
      const map = mapRef.current;
      if (map) clearWalkRouteFromMap(map);
      showToast('🎉 Hedefe ulaştınız!');
    }
  }, [userPos]);

  const handleGPS = () => {
    if (!navigator.geolocation) {
      alert('Tarayıcınız konum desteklemiyor');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { longitude, latitude } = pos.coords;
        console.log('[GPS] Konum alındı:', latitude, longitude);
        setUserPos({ lng: longitude, lat: latitude });
        setSortBy('distance');
        setLocLabel('Konumunuz');
        setMobileTab('map');
        setTimeout(() => {
          try {
            if (mapRef.current) {
              mapRef.current.resize();
              mapRef.current.flyTo({ center: [longitude, latitude], zoom: 14, duration: 1000 });
            }
          } catch(e) {}
        }, 100);
        localStorage.setItem('okulmap_location_type', 'gps');
        showToast('📍 Konumunuz bulundu!');
      },
      (err) => {
        console.error('[GPS] Hata:', err.code, err.message);
        if (err.code === 1) {
          setShowLocPermModal(true);
        } else {
          alert(`Konum hatası (${err.code}): ${err.message}`);
        }
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  };

  const openLocationModal = () => {
    setLocModalStep('choose');
    setLocModalIl('');
    setLocModalIlce('');
    setLocModalIlQuery('');
    setLocModalGpsError('');
    setShowLocationModal(true);
  };

  // ─── FILTERED LIST ──────────────────────────────────────────────────────────
  const upLng = userPos ? (Array.isArray(userPos) ? userPos[0] : userPos.lng) : null;
  const upLat = userPos ? (Array.isArray(userPos) ? userPos[1] : userPos.lat) : null;

  const filtered = useMemo(() => {
    let list = places.map(p => {
      const d = (upLat != null && upLng != null) ? hav(upLat, upLng, p.lat, p.lng) : 1;
      return {...p, distNum: d, distLabel: (upLat != null) ? fmtD(d) : '—'};
    });
    if (activeCategory !== 'all') list = list.filter(p => p.type === activeCategory);
    const normTR = s => (s||'').replace(/İ/g,'i').replace(/I/g,'i').replace(/Ğ/g,'g').replace(/Ş/g,'s').replace(/Ü/g,'u').replace(/Ö/g,'o').replace(/Ç/g,'c').replace(/ğ/g,'g').replace(/ş/g,'s').replace(/ü/g,'u').replace(/ö/g,'o').replace(/ç/g,'c').replace(/ı/g,'i').toLowerCase();
    if (selectedIl) list = list.filter(p => normTR(p.city) === normTR(selectedIl));
    if (selectedIlce && ilceBbox) {
      list = list.filter(p =>
        p.lng >= ilceBbox[0] && p.lat >= ilceBbox[1] &&
        p.lng <= ilceBbox[2] && p.lat <= ilceBbox[3]
      );
    } else if (selectedIlce && !ilceBbox) {
      // bbox not yet loaded — keep current list (don't wipe everything)
    }
    if (selectedMahalleler.length > 0) {
      list = list.filter(p => selectedMahalleler.some(m => hav(m.center[1], m.center[0], p.lat, p.lng) < 1.5));
    }
    if (priceRange[0] > 0 || priceRange[1] < 6000) {
      list = list.filter(p => p.priceNum === 0 || (p.priceNum >= priceRange[0] && p.priceNum <= priceRange[1]));
    }
    if (radiusKm && upLat != null) {
      list = list.filter(p => hav(upLat, upLng, p.lat, p.lng) <= radiusKm);
    }
    if (selectedAgeGroup) {
      const [minA, maxA] = selectedAgeGroup === '12+' ? [12, 99] : selectedAgeGroup.split('-').map(Number);
      list = list.filter(p => p.ageMax >= minA && p.ageMin <= maxA);
    }
    list.sort((a,b) => {
      // Öne çıkanlar her zaman üstte
      if (a.sponsored && !b.sponsored) return -1;
      if (!a.sponsored && b.sponsored) return 1;
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'price') return a.priceNum - b.priceNum;
      return a.distNum - b.distNum;
    });
    return list;
  }, [places, activeCategory, sortBy, userPos, selectedMahalleler, priceRange, selectedAgeGroup, radiusKm, selectedIl, selectedIlce, ilceBbox]);

  // PERF: filter/search değişince liste pagination'ı sıfırla
  useEffect(() => { setListLimit(50); }, [activeCategory, search, sortBy, selectedIl, selectedIlce, priceRange, selectedAgeGroup, radiusKm]);

  // search filtresi sadece liste paneli için (pinler haritada görünmeye devam eder)
  const displayFiltered = useMemo(() => {
    if (!search.trim()) return filtered;
    const trNorm = s => (s||'').toLowerCase().replace(/ı/g,'i').replace(/ş/g,'s').replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ö/g,'o').replace(/ç/g,'c');
    const q = trNorm(search);
    return filtered.filter(p => trNorm(p.name).includes(q) || trNorm(p.city||'').includes(q) || trNorm(p.district||'').includes(q) || trNorm(p.neighborhood||'').includes(q) || trNorm(p.type||'').includes(q));
  }, [filtered, search]);

  // Skor cache — matchScore'u her render'da yeniden hesaplama, prefs/places değişince güncelle
  const scoreCache = useMemo(() => {
    if (!userPrefs) return null;
    const m = new Map();
    places.forEach(p => { m.set(p.id, matchScore(p, userPrefs)); });
    return m;
  }, [places, userPrefs]);
  const getScore = useCallback((p) => scoreCache ? scoreCache.get(p.id) : null, [scoreCache]);

  // ─── SUPABASE AUTH ──────────────────────────────────────────────────────────
  // Click-outside: il/ilçe dropdown'larını kapat
  useEffect(() => {
    const handler = (e) => {
      if (ilBtnRef.current && !ilBtnRef.current.contains(e.target)) setIlDropdownOpen(false);
      if (ilceBtnRef.current && !ilceBtnRef.current.contains(e.target)) setIlceDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    // OAuth redirect'ten dönen hash token'ı manuel işle
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const access_token = params.get('access_token');
      const refresh_token = params.get('refresh_token');
      if (access_token && refresh_token) {
        supabase.auth.setSession({ access_token, refresh_token }).then(({ data, error }) => {
          if (!error && data?.session?.user) {
            const u = data.session.user;
            localStorage.removeItem('okulmap_signed_out');
            setUser({name: u.user_metadata?.full_name || u.email, email: u.email, id: u.id});
            setShowAuth(false);
          }
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        });
      }
    }

    // Staff rolünde olan kullanıcılar app.okulmap.com'a yönlendirilir.
    // localStorage 'okulmap_skip_admin_redirect=1' ise atla (test/development için).
    const STAFF_ROLES = ['mudur', 'yonetici', 'ogretmen', 'idare', 'super_admin'];
    // Cross-subdomain SSO: access + refresh token'ı URL hash'inde taşı; app.okulmap.com
    // /auto-login köprüsü hash'i parse edip aynı oturumu kuruyor (tekrar login istemez).
    const buildAdminUrl = (session) => {
      const at = session?.access_token;
      const rt = session?.refresh_token;
      if (at && rt) {
        return `https://app.okulmap.com/auto-login#access_token=${encodeURIComponent(at)}&refresh_token=${encodeURIComponent(rt)}`;
      }
      return 'https://app.okulmap.com/login';
    };
    const redirectStaffIfNeeded = async (authUserId, opts = {}) => {
      if (!authUserId) return false;
      try {
        const { data } = await supabase.from('users').select('role').eq('auth_id', authUserId).maybeSingle();
        if (data && STAFF_ROLES.includes(data.role)) {
          const { data: sessData } = await supabase.auth.getSession();
          const url = buildAdminUrl(sessData?.session);
          // staffInfo her zaman set — profile menüsünde "Panele git" görünebilsin
          setStaffInfo({ role: data.role, url });
          // DÖNGÜ FIX'İ: panelden gelen ziyaret (?from=panel) veya okul profil
          // görüntüleme (?okul=<slug>) sırasında panele GERİ FIRLATMA — okul sahibi
          // kendi public profilini görebilsin. "Panele git" menüsü yine erişilebilir.
          const qs = new URLSearchParams(window.location.search);
          const stayHere = qs.has('okul') || qs.get('from') === 'panel';
          if (opts.hard && !stayHere && localStorage.getItem('okulmap_skip_admin_redirect') !== '1') {
            window.location.href = url;
          }
          return true;
        } else {
          setStaffInfo(null);
        }
      } catch (e) { console.warn('[okulmap] role check error:', e); }
      return false;
    };

    supabase.auth.getSession().then(res => {
      const u = res.data?.session?.user;
      if (u && !localStorage.getItem('okulmap_signed_out')) {
        setUser({name: u.user_metadata?.full_name || u.email, email: u.email, id: u.id});
        redirectStaffIfNeeded(u.id, { hard: false });
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const u = session.user;
        localStorage.removeItem('okulmap_signed_out');
        setUser({name: u.user_metadata?.full_name || u.email, email: u.email, id: u.id});
        // Yeni login → app.okulmap.com'a hard redirect
        if (_event === 'SIGNED_IN') {
          redirectStaffIfNeeded(u.id, { hard: true });
        }
      } else {
        setUser(null);
        setStaffInfo(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ─── SLUG HELPERS ─────────────────────────────────────────────────────────────
  const toSlug = (name) =>
    name.toLowerCase()
      .replace(/[ğ]/g,'g').replace(/[ü]/g,'u').replace(/[ş]/g,'s')
      .replace(/[ı]/g,'i').replace(/[ö]/g,'o').replace(/[ç]/g,'c')
      .replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');

  const generateUniqueSlug = async (name) => {
    const base = toSlug(name);
    const { data } = await supabase.from('schools').select('slug').like('slug', base + '%');
    const existing = (data || []).map(r => r.slug).filter(Boolean);
    if (!existing.includes(base)) return base;
    let n = 2;
    while (existing.includes(`${base}-${n}`)) n++;
    return `${base}-${n}`;
  };

  // ─── URL PUSHSTATE + ANALYTICS TRACKING ─────────────────────────────────────
  useEffect(() => {
    if (detailId) {
      detailEverOpenedRef.current = true;
      const p = places.find(x => String(x.id) === String(detailId));
      if (p) {
        const apply = async () => {
          let slug = p.slug;
          if (!slug) {
            // DB'de slug var mı önce kontrol et (hafızada yoksa)
            const { data: dbRow } = await supabase.from('schools').select('slug').eq('id', detailId).single();
            if (dbRow?.slug) {
              slug = dbRow.slug;
            } else {
              slug = await generateUniqueSlug(p.name);
              await supabase.from('schools').update({ slug }).eq('id', detailId);
            }
            setPlaces(prev => prev.map(x => String(x.id) === String(detailId) ? { ...x, slug } : x));
            placesRef.current = placesRef.current.map(x => String(x.id) === String(detailId) ? { ...x, slug } : x);
          }
          window.history.pushState({schoolId: detailId}, p.name, '?okul=' + slug);
          document.title = p.name + ' — OkulMap';
          trackAnalytics(detailId, 'view');
          if (typeof window.gtag === 'function') window.gtag('event', 'page_view', { page_title: p.name, page_location: window.location.href });
          supabase.from('school_badges').select('*').eq('school_id', detailId)
            .then(res => setSchoolBadges(res.data || []));
        };
        apply();
      }
    } else {
      // Sadece daha önce bir okul açılmışsa URL'i sıfırla
      // İlk render'da (detailId hiç set edilmemişken) ?okul= parametresini silme
      if (detailEverOpenedRef.current) {
        window.history.replaceState({}, 'OkulMap', '/');
      }
      document.title = 'OkulMap — İstanbul Anaokul, Kreş ve Özel Okul Ara';
      setSchoolBadges([]);
    }
  }, [detailId]); // eslint-disable-line

  // ─── SEO LANDING PATH (/anaokul/uskudar gibi) ────────────────────────────────
  // Mount'ta path'i parse et → kategori + ilçe filtresi pre-set, meta tag güncelle
  useEffect(() => {
    const seo = parseSeoPath(window.location.pathname);
    if (!seo) return;
    applySeoMeta(seo);
    if (seo.type) setActiveCategory(seo.type);
    if (seo.ilce) {
      setSelectedIlce(seo.ilce.charAt(0).toUpperCase() + seo.ilce.slice(1).toLowerCase());
    }
    // tag (ör. montessori) — search input'a dolduralım, kullanıcı genişletebilir
    if (seo.tag) setSearch(seo.tag);
  }, []); // sadece mount'ta — path değişimi kullanıcı navigasyonu

  // ─── URL DEEP LINK ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (places.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const slug = (params.get('okul') || params.get('school') || '').split('&')[0].trim() || null;
    if (!slug) return;
    // 1. Exact match
    const exact = places.find(p => p.slug === slug);
    if (exact) { setDetailId(exact.id); return; }
    // 2. Variant match: slug-2, slug-3 vb. (DB'de suffix eklenmiş olabilir)
    const variant = places.find(p => p.slug && p.slug.startsWith(slug + '-'));
    if (variant) { setDetailId(variant.id); return; }
    // 3. Supabase fallback — listed olan okulu bul
    supabase.from('schools').select('id').eq('slug', slug).eq('okulmap_listed', true).maybeSingle()
      .then(({ data }) => {
        if (data) { setDetailId(data.id); return; }
        // 4. Listed olmayan ama slug eşleşen (yedek)
        supabase.from('schools').select('id').eq('slug', slug).maybeSingle()
          .then(({ data: d }) => { if (d) setDetailId(d.id); });
      });
  }, [places]);

  // ─── URL ?compare= DEEP LINK ─────────────────────────────────────────────────
  useEffect(() => {
    if (places.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('compare');
    if (!raw) return;
    const tokens = raw.split(',').map(s=>s.trim()).filter(Boolean).slice(0,3);
    if (!tokens.length) return;
    const ids = tokens.map(t => {
      const bySlug = places.find(p => p.slug === t);
      if (bySlug) return bySlug.id;
      const variant = places.find(p => p.slug && p.slug.startsWith(t + '-'));
      if (variant) return variant.id;
      const byId = places.find(p => String(p.id) === String(t));
      return byId ? byId.id : null;
    }).filter(Boolean);
    if (ids.length > 0) {
      setCompareList(ids);
      setShowCompare(true);
    }
  }, [places]);

  // ─── WebMCP — sayfa içi AI agent tool'ları ──────────────────────────────────
  useEffect(() => {
    if (!navigator.modelContext) return; // Desteklenmiyor, çık

    navigator.modelContext.provideContext({
      tools: [
        {
          name: "search_schools",
          description: "Türkiye genelinde okul ara. İlçe, şehir, tür, fiyat ve yaş aralığına göre filtrele.",
          inputSchema: {
            type: "object",
            properties: {
              district: { type: "string", description: "İlçe adı" },
              city: { type: "string", description: "Şehir adı" },
              type: { type: "string", description: "Okul türü: anaokul, kres, oyunevi, muzik, spor" },
              max_price: { type: "number", description: "Maksimum aylık ücret (TL)" }
            }
          },
          execute: async (input) => {
            // Mevcut places array'ini filtrele
            let results = places;
            if (input.district) results = results.filter(p => p.district?.toLowerCase().includes(input.district.toLowerCase()));
            if (input.city) results = results.filter(p => p.city?.toLowerCase().includes(input.city.toLowerCase()));
            if (input.type) results = results.filter(p => p.type === input.type);
            if (input.max_price) results = results.filter(p => !p.monthly_price || p.monthly_price <= input.max_price);
            return results.slice(0, 10).map(p => ({ name: p.name, type: p.type, district: p.district, city: p.city, price: p.monthly_price, slug: p.slug }));
          }
        },
        {
          name: "open_school_detail",
          description: "Belirli bir okulun profilini aç",
          inputSchema: {
            type: "object",
            properties: {
              slug: { type: "string", description: "Okul slug'ı" }
            },
            required: ["slug"]
          },
          execute: async (input) => {
            const school = places.find(p => p.slug === input.slug);
            if (school) setDetailId(school.id);
            return { success: !!school };
          }
        }
      ]
    });
  }, [places]);

  // ─── LOAD FAVORITES ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setFavs([]); return; }
    supabase.from('favorites').select('school_id').eq('user_id', user.id)
      .then(res => {
        if (res.data) setFavs(res.data.map(f => f.school_id));
      });
  }, [user]);

  // ─── LOAD USER PROFILE ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setUserProfile(null); return; }
    supabase.from('users').select('name,phone').eq('auth_id', user.id).maybeSingle()
      .then(res => { if (res.data) setUserProfile(res.data); });
  }, [user]);

  // ─── LOAD CAMPAIGNS ─────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.from('campaigns').select('*').eq('is_active', true).order('created_at',{ascending:false}).limit(10)
      .then(res => { if (res.data) setCampaigns(res.data); });

    // Kampanya impression tracking — sayfa session'da bir kere bump et
    // (impressions sütunu yoksa sessizce başarısız olur, RPC ile atomic increment)
    setTimeout(() => {
      try {
        const seen = JSON.parse(sessionStorage.getItem('okulmap_campaign_imps') || '[]');
        supabase.from('campaigns').select('id').eq('is_active', true).then(({ data }) => {
          if (!data) return;
          data.forEach(c => {
            if (!seen.includes(c.id)) {
              supabase.rpc('increment_campaign_impression', { campaign_id: c.id })
                .catch(() => {/* RPC yoksa sessizce atla */});
              seen.push(c.id);
            }
          });
          sessionStorage.setItem('okulmap_campaign_imps', JSON.stringify(seen));
        });
      } catch {}
    }, 1500);
  }, []);

  // ─── LOAD EVENTS ─────────────────────────────────────────────────────────────
  const loadEvents = () => {
    const today = new Date();
    const twoWeeksLater = new Date(today);
    twoWeeksLater.setDate(today.getDate() + 14);
    const from = today.toISOString().split('T')[0];
    const to = twoWeeksLater.toISOString().split('T')[0];
    supabase.from('events').select('*')
      .eq('is_active', true)
      .gte('date', from)
      .lte('date', to)
      .order('date')
      .then(res => { if (res.data) setEventsData(res.data); });
  };
  useEffect(() => { loadEvents(); }, []);

  window.__okulmap_reserveEvent = (evId) => {
    const ev = eventsData.find(e => String(e.id) === String(evId));
    if (ev) { setEventReserveModal(ev); setReserveForm({name:'',phone:'',guest_count:'1',note:''}); setReserveMsg(null); }
  };

  const submitReservation = async () => {
    if (!eventReserveModal) return;
    if (!reserveForm.name.trim()) { setReserveMsg('Ad soyad zorunludur.'); return; }
    if (!reserveForm.phone.trim()) { setReserveMsg('Telefon zorunludur.'); return; }
    setReserveSaving(true);
    const { error } = await supabase.from('event_reservations').insert({
      event_id: eventReserveModal.id,
      school_id: eventReserveModal.school_id || null,
      name: reserveForm.name.trim(),
      phone: reserveForm.phone.trim(),
      guest_count: Number(reserveForm.guest_count) || 1,
      note: reserveForm.note.trim() || null,
      status: 'pending',
    });
    setReserveSaving(false);
    if (error) { setReserveMsg('Hata: ' + error.message); return; }
    setReserveMsg('success');
  };

  // ─── LOAD ALL SCHOOL BADGES ──────────────────────────────────────────────────
  useEffect(() => {
    supabase.from('school_badges').select('school_id,badge_key,verified').then(res => {
      if (!res.data) return;
      const map = {};
      res.data.forEach(b => {
        if (!map[b.school_id]) map[b.school_id] = [];
        map[b.school_id].push({key: b.badge_key, verified: b.verified});
      });
      setAllSchoolBadges(map);
    });
  }, []);

  // ─── PWA + SERVICE WORKER ────────────────────────────────────────────────────
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
    const handler = (e) => {
      e.preventDefault();
      pwaPromptRef.current = e;
      if (!localStorage.getItem('okulmap_pwa_dismissed')) setShowPwaPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ─── İLÇE BBOX — geocode to get bounding box for geographic filtering ────────
  useEffect(() => {
    if (!selectedIlce || !selectedIl) { setIlceBbox(null); return; }
    const query = encodeURIComponent(selectedIlce + ', ' + selectedIl + ', Türkiye');
    fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?country=TR&language=tr&limit=1&access_token=${MAPBOX_TOKEN}`)
      .then(r => r.json())
      .then(d => {
        const feat = d.features?.[0];
        if (feat?.bbox) {
          setIlceBbox(feat.bbox); // [minLng, minLat, maxLng, maxLat]
        } else if (feat?.center) {
          // No bbox, approximate ~0.1 degree radius
          const [lng, lat] = feat.center;
          setIlceBbox([lng - 0.1, lat - 0.1, lng + 0.1, lat + 0.1]);
        } else {
          setIlceBbox(null);
        }
      })
      .catch(() => setIlceBbox(null));
  }, [selectedIlce, selectedIl]);

  // ─── MAHALLE LIST — yerel JSON (/public/data/mahalleler.json) ────────────────
  useEffect(() => {
    if (!selectedIlce) { setMahalleResults([]); setMahalleQuery(''); setMahallePanelOpen(false); return; }
    setMahalleSearching(true);
    getMahalleData()
      .then(data => {
        const names = (data[selectedIl]?.[selectedIlce] || []);
        setMahalleResults(names.map(name => ({name, center: null})));
        setMahalleSearching(false);
      })
      .catch(() => setMahalleSearching(false));
  }, [selectedIlce, selectedIl]);

  // ─── LOAD PLACES FROM SUPABASE ─────────────────────────────────────────────
  // Supabase default limit 1000 — schools 1000+ olduğu için pagination
  // şart. .range() ile 1000'lik batch'lerle hepsini topluyoruz.
  useEffect(() => {
    const SELECT = 'id,name,type,lat,lng,address,city,district,neighborhood,rating,review_count,age_min,age_max,monthly_price,capacity,enrolled_count,avg_class_size,teacher_child_ratio,hours,features,description,okulbot_url,okulmap_listed,okulmap_featured,is_verified,owner_claimed,photos,phone,email,website';
    const PAGE = 1000;
    const fetchAll = async () => {
      const all = [];
      let from = 0;
      let lastError = null;
      while (true) {
        const { data, error } = await supabase
          .from('schools')
          .select(SELECT)
          .eq('okulmap_listed', true)
          .range(from, from + PAGE - 1);
        if (error) { lastError = error; break; }
        if (!data || data.length === 0) break;
        all.push(...data);
        if (data.length < PAGE) break;
        from += PAGE;
      }
      return { data: all, error: lastError };
    };
    fetchAll()
      .then(async (res) => {
        console.log('[Places] error:', res.error);
        console.log('[Places] loaded:', res.data?.length ?? 0);
        if (!res.data || !res.data.length) { setLoading(false); return; }
        const TYPE_MAP = {kreş:'anaokul',anaokul:'anaokul',okul:'anaokul',ilkokul:'anaokul',oyunevi:'oyunevi',kurs:'kurs',atölye:'kurs',ozel:'ozel','özel':'ozel',ozel_egitim:'ozel',spor:'spor',muzik:'muzik','müzik':'muzik',dans:'muzik'};
        const loaded = [];
        for (const s of res.data) {
          let lat = s.lat, lng = s.lng;
          if (lat == null || lng == null) {
            const query = [s.address, s.city, 'Türkiye'].filter(Boolean).join(' ');
            if (!query.trim()) { lat = 40.9898; lng = 29.0272; }
            else {
              try {
                const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=TR&limit=1`);
                const geo = await r.json();
                if (geo.features?.[0]) {
                  lng = geo.features[0].center[0];
                  lat = geo.features[0].center[1];
                  await supabase.from('schools').update({lat, lng}).eq('id', s.id);
                } else { lat = 40.9898; lng = 29.0272; }
              } catch(e) { lat = 40.9898; lng = 29.0272; }
            }
          }
          let feats = [];
          if (s.features) {
            if (Array.isArray(s.features)) { feats = s.features; }
            else { try { feats = JSON.parse(s.features); } catch(e) { feats = []; } }
          }
          loaded.push({
            id: s.id, name: s.name || 'İsimsiz Kurum', type: TYPE_MAP[s.type] || TYPE_MAP[(s.type||'').toLowerCase()] || 'anaokul',
            lng, lat, rating: s.rating || 4.0, reviews: s.review_count || 0,
            ageMin: s.age_min || 0, ageMax: s.age_max || 18,
            age: (s.age_min||0)+'-'+(s.age_max||18),
            price: s.monthly_price ? 'Ort. ₺'+s.monthly_price.toLocaleString('tr-TR')+'/ay' : 'Fiyat için iletişime geçin',
            priceNum: s.monthly_price || 0, tags: [],
            open: (() => {
              const h = s.hours || '';
              const m = h.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
              if (!m) return true;
              const now = new Date();
              const turkeyHour = (now.getUTCHours() + 3) % 24;
              const turkeyMin = now.getUTCMinutes();
              const cur = turkeyHour * 60 + turkeyMin;
              const open = parseInt(m[1]) * 60 + parseInt(m[2]);
              const close = parseInt(m[3]) * 60 + parseInt(m[4]);
              return cur >= open && cur < close;
            })(),
            capacity: s.capacity || 50, filled: s.enrolled_count || 0,
            avgClassSize: s.avg_class_size || null,
            teacherChildRatio: s.teacher_child_ratio || null,
            hours: s.hours || '08:00-18:00', desc: s.description || '',
            features: feats, photos: s.photos && s.photos.length > 0 ? s.photos : ['🏫'], sponsored: s.okulmap_featured || false,
            okulbot: s.okulbot_url || null,
            is_verified: s.is_verified || false,
            phone: s.phone || null, email: s.email || null, website: s.website || null,
            address: s.address || null, city: s.city || null, district: s.district || null, neighborhood: s.neighborhood || null
          });
        }
        placesRef.current = loaded;
        setPlaces(loaded);
        setLoading(false);
      })
      .catch(err => { Sentry.captureException(err); console.error(err); setLoading(false); });
  }, []);

  // ─── SUPABASE REALTIME: schools UPDATE → pin güncelle ───────────────────────
  useEffect(() => {
    const TYPE_MAP = {kreş:'anaokul',anaokul:'anaokul',okul:'anaokul',ilkokul:'anaokul',oyunevi:'oyunevi',kurs:'kurs',atölye:'kurs',ozel:'ozel','özel':'ozel',ozel_egitim:'ozel',spor:'spor',muzik:'muzik','müzik':'muzik',dans:'muzik'};

    const geocode = async (address, city) => {
      const query = [address, city, 'Türkiye'].filter(Boolean).join(' ');
      if (!query.trim()) return { lat: 40.9898, lng: 29.0272 };
      try {
        const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=TR&limit=1`);
        const geo = await r.json();
        if (geo.features?.[0]) return { lng: geo.features[0].center[0], lat: geo.features[0].center[1] };
      } catch(e) {}
      return { lat: 40.9898, lng: 29.0272 };
    };

    const channel = supabase
      .channel('schools-realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'schools' }, async (payload) => {
        const s = payload.new;
        console.log('[Realtime] UPDATE:', s.id, s.name, 'address:', s.address);

        // Listelenmiyorsa listeden çıkar
        if (!s.okulmap_listed) {
          placesRef.current = placesRef.current.filter(p => String(p.id) !== String(s.id));
          setPlaces(prev => prev.filter(p => String(p.id) !== String(s.id)));
          return;
        }

        // Mevcut okulu bul — adres değişti mi kontrol et
        const existing = placesRef.current.find(p => String(p.id) === String(s.id));
        const addressChanged = !existing || existing.address !== s.address || existing.city !== s.city;
        const noCoords = s.lat == null || s.lng == null;

        let lat = s.lat, lng = s.lng;

        if (noCoords || addressChanged) {
          // Adres değiştiyse yeniden geocode yap, DB'ye yaz
          const coords = await geocode(s.address, s.city);
          lat = coords.lat; lng = coords.lng;
          if (addressChanged) {
            // Yeni koordinatları DB'ye kaydet
            await supabase.from('schools').update({ lat, lng }).eq('id', s.id);
          }
        }

        const updated = {
          ...(existing || {}),
          id: s.id,
          name: s.name || 'İsimsiz Kurum',
          type: TYPE_MAP[s.type] || TYPE_MAP[(s.type||'').toLowerCase()] || 'anaokul',
          lat, lng,
          rating: s.rating || 4.0,
          reviews: s.review_count || 0,
          ageMin: s.age_min || 0, ageMax: s.age_max || 18,
          age: (s.age_min||0)+'-'+(s.age_max||18),
          price: s.monthly_price ? 'Ort. ₺'+s.monthly_price.toLocaleString('tr-TR')+'/ay' : 'Fiyat için iletişime geçin',
          priceNum: s.monthly_price || 0,
          capacity: s.capacity || 50, filled: s.enrolled_count || 0,
          avgClassSize: s.avg_class_size || null,
          teacherChildRatio: s.teacher_child_ratio || null,
          hours: s.hours || '08:00-18:00', desc: s.description || '',
          photos: s.photos?.length > 0 ? s.photos : ['🏫'],
          sponsored: s.okulmap_featured || false,
          okulbot: s.okulbot_url || null,
          is_verified: s.is_verified || false,
          phone: s.phone || null, email: s.email || null, website: s.website || null,
          address: s.address || null, city: s.city || null,
        };

        if (existing) {
          placesRef.current = placesRef.current.map(p => String(p.id) === String(s.id) ? updated : p);
          setPlaces(prev => prev.map(p => String(p.id) === String(s.id) ? updated : p));
        } else {
          placesRef.current = [...placesRef.current, updated];
          setPlaces(prev => [...prev, updated]);
        }
      })
      .subscribe((status) => {
        console.log('[Realtime] status:', status);
      });

    return () => { supabase.removeChannel(channel); };
  }, []);

  // ─── MAP INIT ───────────────────────────────────────────────────────────────
  const mapInitializedRef = useRef(false);
  useEffect(() => {
    // Onboarding modalı açıkken haritayı arkada init etme — telefon kasıyor
    if (showOnboarding) return;
    if (!mapContainerRef.current || mapInitializedRef.current) return;
    const initMap = () => {
      if (mapRef.current) return;
      const mapboxgl = window.mapboxgl;
      if (!mapboxgl) return;
      mapInitializedRef.current = true;
      window.__okulmap_openDetail = (id) => {
        setDetailId(String(id));
        setRecentIds(prev => {
          const next = [String(id), ...prev.filter(x => x !== String(id))].slice(0, 10);
          try { localStorage.setItem('okulmap_recent', JSON.stringify(next)); } catch(e) {}
          return next;
        });
      };
      window.__okulmap_compare = (id) => {
        setCompareList(prev => {
          if (prev.includes(id)) return prev.filter(x => x !== id);
          if (prev.length >= 3) return prev;
          return [...prev, id];
        });
      };
      mapboxgl.accessToken = MAPBOX_TOKEN;
      try {
        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/dark-v11',
          center: [28.9784, 41.0082],
          zoom: 10,
          pitch: 0,                  // 20→0: 3D tilt yok, GPU per-frame iş %30 azalır
          renderWorldCopies: false,  // dünya kopyaları render yok (mobil tile tasarrufu)
          maxZoom: 18,
          fadeDuration: 0,           // label/feature fade animation'ı kapat (iOS Safari'de pahalı)
        });
        mapRef.current = map;
        // Boş haritaya tıklayınca/dokununca aktif popup'ı kapat
        // (cluster/pin event'leri stopPropagation eder, bu yüzden bu sadece boş alana erişir)
        map.on('click', () => { closeActivePopup(); });
        // Mobilde touchend (Mapbox style v3'te touchend yok, dragend/touchcancel kullan)
        map.on('movestart', () => { /* drag başlarken popup kalsın */ });
        map.addControl(new mapboxgl.NavigationControl({showCompass:false}), 'bottom-right');
        const geo = new mapboxgl.GeolocateControl({positionOptions:{enableHighAccuracy:true},trackUserLocation:true,showUserHeading:true});
        map.addControl(geo, 'bottom-right');
        geo.on('geolocate', (e) => {
          const newLng = e.coords.longitude;
          const newLat = e.coords.latitude;
          const prev = userPosRef.current;
          if (prev) {
            const oldLng = Array.isArray(prev) ? prev[0] : prev.lng;
            const oldLat = Array.isArray(prev) ? prev[1] : prev.lat;
            const dist = Math.sqrt(Math.pow(newLng - oldLng, 2) + Math.pow(newLat - oldLat, 2));
            if (dist <= 0.0005) return; // ~50 metre, güncelleme yok
          }
          setUserPos([newLng, newLat]);
          setSortBy('distance');
          setLocLabel('Konumunuz');
        });
        map.on('load', () => {
          map.getStyle().layers.forEach(l => {
            if (l.id.includes('road') && l.type === 'symbol') map.setLayoutProperty(l.id, 'visibility', 'none');
          });
          addBaseLayers(map);
          setMapLoaded(true);
          const ld = document.getElementById('map-loading');
          if (ld) ld.style.display = 'none';
          // ── Zoom-driven pin size — cluster KALDIRILDI ──────────────────
          const applyZoom = () => {
            const z = map.getZoom();
            clusterHidingRef.current = false;
            const hideForCluster = z < 8; // z<8 choropleth, z>=8 pin
            const sz = z < 9 ? '20px' : z < 11 ? '26px' : z < 13 ? '34px' : '42px';
            const fs = z < 9 ? '10px' : z < 11 ? '13px' : z < 13 ? '17px' : '20px';
            const ds = z < 9 ? '4px'  : z < 11 ? '5px'  : z < 13 ? '5px'  : '6px';
            Object.keys(markersRef.current).filter(k => !k.startsWith('t_')).forEach(k => {
              const mk = markersRef.current[k];
              if (!mk) return;
              const el2 = mk.getElement();
              if (!el2) return;
              if (modeRef.current === 'heat') return;
              if (hideForCluster) {
                // visibility ile gizle — display:none CSS animasyonunu iOS'te yeniden başlatıyor
                el2.style.visibility = 'hidden';
                el2.style.pointerEvents = 'none';
                return;
              }
              el2.style.visibility = 'visible';
              el2.style.opacity = '1';
              el2.style.pointerEvents = 'all';
              const diamond = el2.querySelector('[data-pin-diamond]');
              if (diamond) { diamond.style.width = sz; diamond.style.height = sz; }
              const emoji2 = el2.querySelector('[data-pin-emoji]');
              if (emoji2) emoji2.style.fontSize = fs;
              const dot = el2.querySelector('[data-pin-dot]');
              if (dot) { dot.style.width = ds; dot.style.height = ds; }
              const meta = el2.querySelector('[data-pin-meta]');
              if (meta) meta.style.display = z < 13 ? 'none' : 'flex';
            });
          };
          applyZoomRef.current = applyZoom;
          map.on('zoom', applyZoom);
          map.on('zoomend', applyZoom);
        });
        map.on('error', (e) => { Sentry.captureException(e.error); console.warn('Mapbox error:', e); });
      } catch(err) {
        console.warn('Map init error:', err);
        const ld = document.getElementById('map-loading');
        if (ld) ld.innerHTML = '<div style="color:rgba(255,255,255,0.25);font-size:12px;text-align:center;padding:30px 20px;">🗺<br><br>Harita yüklenemedi.</div>';
      }
    };

    if (window.mapboxgl) { initMap(); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.onload = initMap;
    script.onerror = () => {
      const ld = document.getElementById('map-loading');
      if (ld) ld.innerHTML = '<div style="color:rgba(255,255,255,0.25);font-size:13px;text-align:center;padding:40px 20px;line-height:1.8;">🗺<br><br>Harita yüklenemedi.<br><span style="font-size:11px;opacity:0.6;">İnternet bağlantısını kontrol edin<br>veya VPN kullanmayı deneyin.</span></div>';
    };
    document.head.appendChild(script);
  }, [showOnboarding]);


  // ─── ADD BASE LAYERS ────────────────────────────────────────────────────────
  function addBaseLayers(map) {
    // Cluster KALDIRILDI — z<8 choropleth, z>=8 direkt HTML pin
    // places-src yine duruyor (auto-heat-src ve diğer logic için referans
    // edilebilir) ama cluster:true OLMADAN. Cluster layer'ları yok.
    map.addSource('places-src', {
      type:'geojson',
      data:{type:'FeatureCollection',features: places.map(p => ({type:'Feature',geometry:{type:'Point',coordinates:[p.lng,p.lat]},properties:{id:p.id,type:p.type}}))},
    });
    // Auto heat overlay — sadece zoom out'ta (z<11), normal modda görünür
    map.addSource('auto-heat-src', {
      type:'geojson',
      data:{type:'FeatureCollection',features: places.filter(p=>p.lat&&p.lng).map(p=>({type:'Feature',geometry:{type:'Point',coordinates:[p.lng,p.lat]},properties:{w:1}}))}
    });
    map.addLayer({id:'auto-heat-lyr',type:'heatmap',source:'auto-heat-src',maxzoom:9,
      layout:{visibility:'none'}, // default kapalı — sadece mode='heat' iken açılır
      paint:{
        'heatmap-radius':['interpolate',['linear'],['zoom'],4,14,9,32],
        'heatmap-opacity':['interpolate',['linear'],['zoom'],4,0.85,8,0.7,9,0],
        'heatmap-intensity':1.2,
        'heatmap-color':['interpolate',['linear'],['heatmap-density'],
          0,'rgba(0,0,255,0)',0.2,'rgba(102,178,255,0.55)',0.5,'rgba(255,230,109,0.7)',0.8,'rgba(255,140,66,0.8)',1,'rgba(255,107,107,0.9)']
      }});
    [{id:'moda',bbox:[[29.019,40.982],[29.038,40.993]],color:'#4ECDC4'},
     {id:'kalamis',bbox:[[29.028,40.976],[29.045,40.982]],color:'#7BE495'},
     {id:'yeldeg',bbox:[[29.010,40.987],[29.024,40.996]],color:'#FFE66D'},
     {id:'hasan',bbox:[[29.022,40.988],[29.034,40.996]],color:'#FF8C42'}
    ].forEach(z => {
      const w=z.bbox[0][0],s=z.bbox[0][1],e=z.bbox[1][0],n=z.bbox[1][1];
      map.addSource('safe-'+z.id,{type:'geojson',data:{type:'Feature',geometry:{type:'Polygon',coordinates:[[[w,s],[e,s],[e,n],[w,n],[w,s]]]}}});
      map.addLayer({id:'safe-fill-'+z.id,type:'fill',source:'safe-'+z.id,paint:{'fill-color':z.color,'fill-opacity':0.18},layout:{visibility:'none'}});
      map.addLayer({id:'safe-line-'+z.id,type:'line',source:'safe-'+z.id,paint:{'line-color':z.color,'line-width':1.5},layout:{visibility:'none'}});
    });
  }

  // ─── MAP MARKERS ────────────────────────────────────────────────────────────
  // Viewport-based rendering: sadece görünen alandaki okullar DOM'da marker
  // olur. Pan/zoom'da debounced 300ms ile yeniden hesaplanır. 100+ okul olsa
  // bile aynı anda ~30-50 marker DOM'da → iOS Safari memory crash önlenir.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.mapboxgl || !mapLoaded) return;
    let cancelled = false;
    let moveDebounceTimer = null;

    const doMarkers = () => {
      if (cancelled) return;
      if (!map.isStyleLoaded()) { setTimeout(doMarkers, 100); return; }

      // Viewport filtresi — bounds'a +%30 buffer ekle.
      const z = map.getZoom();
      const showHtmlMarkers = z >= 8;
      const bounds = map.getBounds();
      const ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
      const latPad = (ne.lat - sw.lat) * 0.3;
      const lngPad = (ne.lng - sw.lng) * 0.3;
      const inViewport = (p) => p.lat >= sw.lat - latPad && p.lat <= ne.lat + latPad
        && p.lng >= sw.lng - lngPad && p.lng <= ne.lng + lngPad;

      // ── PERF: HARD CAP 150 marker. Map center'a en yakın 150. ──
      // İlk mount'ta 1300 marker × ~5ms = 6-7 sn donma sorununu çözer.
      // Pan/zoom sırasında "yeni gelen yakın 150" hesaplanır, fazlası
      // remove edilir. iOS Safari main thread donması büyük ölçüde biter.
      const MAX_MARKERS = 150;
      const center = map.getCenter();
      let visiblePool = showHtmlMarkers ? filtered.filter(inViewport) : [];
      if (visiblePool.length > MAX_MARKERS) {
        // Sponsor + kampanyalı pin'leri önce — sonra mesafeye göre
        visiblePool = visiblePool
          .map(p => ({
            p,
            priority: p.sponsored ? 0 : (campaignsBySchool[p.id] ? 1 : 2),
            dist: (p.lat - center.lat) ** 2 + (p.lng - center.lng) ** 2,
          }))
          .sort((a, b) => a.priority - b.priority || a.dist - b.dist)
          .slice(0, MAX_MARKERS)
          .map(x => x.p);
      }
      const visibleIds = new Set(visiblePool.map(p => String(p.id)));

      // Görünmez alana çıkanları + filter'dan düşenleri DOM'dan kaldır
      Object.keys(markersRef.current).filter(k => !k.startsWith('t_') && !visibleIds.has(k)).forEach(k => {
        markersRef.current[k].remove();
        delete markersRef.current[k];
      });

      // Skip already existing markers — ÖNCEDEN filter (chunked loop için)
      const existingIds = new Set(Object.keys(markersRef.current));
      const __newPins = visiblePool.filter(p => !existingIds.has(String(p.id)));
      // ── PERF: marker creation rAF chunks halinde, max 12ms/chunk.
      // İlk render'da senkron 150 marker = 750ms freeze; chunked = main
      // thread her ~16ms responsive kalır.
      let __pinIdx = 0;
      const __createPin = (p, i) => {
        const baseCampaign = campaignsBySchool[p.id];
        const isEvent = baseCampaign && (baseCampaign.type === 'etkinlik' || baseCampaign.type === 'acik_kapi' || baseCampaign.type === 'event');
        // Safety mode: pin rengi güvenlik skoruna göre
        const safetyColor = (() => {
          if (mode !== 'safety') return null;
          const sc = p.rating >= 4.8 ? 92 : p.rating >= 4.5 ? 80 : p.rating >= 4.2 ? 65 : 50;
          return sc >= 85 ? '#4ECDC4' : sc >= 75 ? '#7BE495' : sc >= 60 ? '#FFE66D' : '#FF8C42';
        })();
        const pinColor = safetyColor || (p.sponsored ? '#FFD700'
          : baseCampaign ? (isEvent ? '#A78BFA' : '#FF9500')
          : (TC[p.type] || '#A8DAFF'));
        const color = safetyColor || (p.sponsored ? '#FFD700' : (TC[p.type] || '#A8DAFF'));
        const cat = CATS.find(c => c.id === p.type) || {};
        const fillPct = Math.round((p.capacity > 0 ? p.filled/p.capacity : 0)*100);
        const fillColor = fillPct>80?'#FF6B6B':fillPct>60?'#FFE66D':'#4ECDC4';
        const pinIcon = cat.icon || '📍';
        const pinSize = '42px';
        const el = document.createElement('div');
        const initiallyHidden = map.getZoom() < 9;
        el.style.cssText = 'cursor:pointer;overflow:visible;pointer-events:none;touch-action:manipulation;'+(initiallyHidden?'visibility:hidden;':'');
        const pulseAnim = baseCampaign && !p.sponsored ? (isEvent ? 'kampanya-pulse-event' : 'kampanya-pulse') : '';
        // Drop animasyonu PIN BAŞINA tek sefer — bu okul daha önce
        // bu session'da drop edildiyse animasyon yok, ilk kez
        // görünüyorsa stagger delay ile düşer.
        const alreadyDropped = droppedPinsRef.current.has(String(p.id));
        const dropAnim = alreadyDropped ? '' : 'animation:pinDropMB 0.5s cubic-bezier(0.34,1.5,0.64,1) '+(i*0.07)+'s both;';
        if (!alreadyDropped) droppedPinsRef.current.add(String(p.id));
        el.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;'+dropAnim+'transition:transform 0.15s;">'
          +(p.sponsored||baseCampaign?'<div style="font-size:11px;color:#FFD700;font-weight:800;line-height:1;margin-bottom:1px;">★</div>':'')
          +'<div data-pin-diamond="1" style="width:'+pinSize+';height:'+pinSize+';background:'+color+';border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2.5px solid rgba(255,255,255,0.7);pointer-events:all;cursor:pointer;'+(pulseAnim?'animation:'+pulseAnim+' 1.5s ease-in-out infinite;':'box-shadow:0 4px 16px '+color+'80;')+'">'
          +'<span data-pin-emoji="1" style="transform:rotate(45deg);font-size:20px;line-height:1;display:block;">'+pinIcon+'</span>'
          +'</div>'
          +'<div data-pin-dot="1" style="width:6px;height:6px;background:'+color+';border-radius:50%;opacity:0.7;margin-top:-1px;"></div>'
          +(()=>{
            const photoCount = (p.photos||[]).filter(x=>typeof x==='string'&&x.startsWith('http')).length;
            const reviewCount = p.reviews||0;
            if (!photoCount && !reviewCount) return '';
            return '<div data-pin-meta="1" style="display:flex;gap:3px;margin-top:3px;align-items:center;justify-content:center;">'
              +(photoCount>0?'<span style="background:rgba(0,0,0,0.65);backdrop-filter:blur(4px);border-radius:6px;padding:1px 5px;font-size:9px;color:#fff;font-weight:600;line-height:1.2;">📷 '+photoCount+'</span>':'')
              +(reviewCount>0?'<span style="background:rgba(0,0,0,0.65);backdrop-filter:blur(4px);border-radius:6px;padding:1px 5px;font-size:9px;color:#fff;font-weight:600;line-height:1.2;">💬 '+reviewCount+'</span>':'')
              +'</div>';
          })()
          +'</div>';
        // İlk drop animasyonu oynadıktan sonra inline animation'ı kaldır —
        // mobile tab switch'te (display:none → block) iOS Safari restart etmesin.
        setTimeout(function(){
          const inner = el.firstElementChild;
          if (inner && inner.style && inner.style.animation && inner.style.animation.indexOf('pinDropMB') >= 0) {
            inner.style.animation = 'none';
          }
        }, (i * 70) + 700);
        // PERF: hover scale CSS'e taşındı (.mapboxgl-marker:hover > div).
        // 150 marker × 2 listener = 300 listener registration tasarrufu.
        // iOS Mapbox tap delay fix + pinch/zoom yanlış tetikleme guard
        const handleMarkerTap = function(){
          const idStr = String(p.id);
          const sameMarker = activePopupRef.current && activePopupRef.current._okulmapId === idStr;
          if (sameMarker) {
            closeActivePopup();
            setSelectedId(null);
            return;
          }
          closeActivePopup();
          const m = markersRef.current[p.id];
          if (m) {
            const popup = m.getPopup();
            if (popup) {
              // ── LAZY POPUP HTML — sadece ilk açılışta inşa et ──
              if (!popup._htmlSet && typeof popup._buildHTML === 'function') {
                popup.setHTML(popup._buildHTML());
                popup._htmlSet = true;
              }
              if (!popup.isOpen()) m.togglePopup();
              popup._okulmapId = idStr;
              activePopupRef.current = popup;
            }
          }
          setSelectedId(idStr);
          map.flyTo({center:[p.lng,p.lat],zoom:Math.max(map.getZoom(),15),offset:[0,120],duration:600});
        };

        // Tap detection — pinch/zoom/pan'ı tap olarak sayma
        const TAP_THRESHOLD = 10;   // px hareket toleransı
        const TAP_TIME_MAX = 300;   // ms
        let touchStart = { x: 0, y: 0, time: 0, fingers: 0 };
        let _tapHandled = false;
        el.addEventListener('touchstart', function(e){
          touchStart = {
            x: e.touches[0]?.clientX || 0,
            y: e.touches[0]?.clientY || 0,
            time: Date.now(),
            fingers: e.touches.length,
          };
        }, { passive: true });
        el.addEventListener('touchmove', function(e){
          // 2+ parmak veya hareket → fingers'ı güncelle (pinch/zoom için)
          if (e.touches.length > touchStart.fingers) touchStart.fingers = e.touches.length;
        }, { passive: true });
        el.addEventListener('touchend', function(e){
          // 2+ parmak (pinch/zoom) → tap sayma
          if (touchStart.fingers > 1) return;
          // Çok uzun süre basılı → tap sayma (long-press)
          if (Date.now() - touchStart.time > TAP_TIME_MAX) return;
          // Parmak kaydırıldı (pan/scroll) → tap sayma
          const t = e.changedTouches && e.changedTouches[0];
          if (t) {
            const dx = Math.abs(t.clientX - touchStart.x);
            const dy = Math.abs(t.clientY - touchStart.y);
            if (dx > TAP_THRESHOLD || dy > TAP_THRESHOLD) return;
          }
          _tapHandled = true;
          e.preventDefault();
          e.stopPropagation();
          handleMarkerTap();
          setTimeout(function(){ _tapHandled = false; }, 400);
        }, { passive: false });
        el.addEventListener('click', function(e){
          // Mobilde touchend zaten halletti — double event önle
          if (_tapHandled) return;
          if ('ontouchstart' in window) return;
          e.stopPropagation();
          handleMarkerTap();
        });
        const campaignBanner = baseCampaign
          ? '<div style="margin-top:8px;padding:6px 8px;background:'+pinColor+'18;border:1px solid '+pinColor+'40;border-radius:7px;font-size:10px;color:'+pinColor+';font-weight:700;">'+pinIcon+' '+escapeHTML(baseCampaign.title)+'</div>'
          : '';
        // ── LAZY POPUP — setHTML deferred to first click (saves ~1ms × 150
        // marker = ~150ms init). _buildHTML closure variable'lara erişir.
        const popup = new window.mapboxgl.Popup({offset:[0,-40],anchor:'bottom',closeOnClick:false,closeButton:false,className:'ob-popup'});
        popup._buildHTML = () => (
          '<div style="padding:12px 14px;font-family:DM Sans,sans-serif;min-width:175px;">'
          +'<div style="font-weight:700;font-size:13px;color:#fff;margin-bottom:4px;">'+escapeHTML(p.name)+'</div>'
          +'<div style="font-size:11px;color:rgba(255,255,255,0.5);margin-bottom:7px;">⭐ '+p.rating+' · 👶 '+p.age+'</div>'
          +(p.priceNum>0
            ? '<span style="background:'+color+'25;border:1px solid '+color+'50;border-radius:5px;padding:2px 8px;font-size:10px;color:'+color+';font-weight:700;">'+escapeHTML(p.price)+'</span>'
            : '<span style="background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.12);border-radius:5px;padding:2px 8px;font-size:10px;color:rgba(255,255,255,0.5);font-weight:500;">'+escapeHTML(p.price)+'</span>')
          +campaignBanner
          +'<div style="margin-top:8px;font-size:10px;color:rgba(255,255,255,0.4);">Doluluk: '+fillPct+'%</div>'
          +'<div style="height:3px;background:rgba(255,255,255,0.1);border-radius:2px;margin-top:3px;overflow:hidden;">'
          +'<div style="height:100%;width:'+fillPct+'%;background:'+fillColor+';border-radius:2px;"></div></div>'
          +'<div style="margin-top:10px;display:flex;gap:6px;">'
          +'<button onclick="window.__okulmap_openDetail(\''+escapeHTML(p.id)+'\')" style="flex:1;padding:7px 0;border-radius:8px;background:'+color+';border:none;color:#000;font-size:12px;font-weight:700;cursor:pointer;font-family:DM Sans,sans-serif;">Detay →</button>'
          +(p.phone && p.owner_claimed?'<a href="https://wa.me/'+encodeURIComponent(p.phone.replace(/\D/g,''))+'?text='+encodeURIComponent('Merhaba, OkulMap\'ten yazıyorum. '+(p.name||'')+' hakkında bilgi almak istiyorum.')+'" target="_blank" rel="noopener" title="WhatsApp" style="width:34px;height:32px;border-radius:8px;background:rgba(37,211,102,0.15);border:1px solid rgba(37,211,102,0.45);cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0;text-decoration:none;"><svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg></a>':'')
          +'<button onclick="window.__okulmap_compare(\''+escapeHTML(p.id)+'\')" title="Karşılaştır" style="width:34px;height:32px;border-radius:8px;background:rgba(168,218,255,0.15);border:1px solid rgba(168,218,255,0.35);cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0;">'
          +'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A8DAFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">'
          +'<circle cx="12" cy="5" r="2"/>'
          +'<path d="M10 5H4M14 5H20"/>'
          +'<path d="M17 21H7"/>'
          +'<path d="M12 7V21"/>'
          +'<path d="M22 14C22 15.6569 20.6569 17 19 17C17.3431 17 16 15.6569 16 14M22 14L19.5 8H18.5L16 14M22 14H16"/>'
          +'<path d="M8 14C8 15.6569 6.65685 17 5 17C3.34315 17 2 15.6569 2 14M8 14L5.5 8H4.5L2 14M8 14H2"/>'
          +'</svg>'
          +'</button>'
          +'</div>'
          +'</div>'
        );
        markersRef.current[p.id] = new window.mapboxgl.Marker({element:el,anchor:'bottom'}).setLngLat([p.lng,p.lat]).setPopup(popup).addTo(map);
        // Popup HTML _buildHTML fonksiyonunda — click anında setHTML çağrılır.
        // Popup kapatılınca activePopupRef'i temizle (X butonu, dış tıklama, programatik)
        popup.on('close', function(){
          if (activePopupRef.current === popup) activePopupRef.current = null;
        });
      };
      // Chunked execution — her rAF tick'inde max 12ms iş, sonra browser'a
      // paint + event handling şansı ver. iOS Safari main thread donmaz.
      const __pinTick = () => {
        if (cancelled) return;
        const __t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        while (__pinIdx < __newPins.length) {
          __createPin(__newPins[__pinIdx], __pinIdx);
          __pinIdx++;
          const __now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
          if (__now - __t0 > 12) break;
        }
        if (__pinIdx < __newPins.length) requestAnimationFrame(__pinTick);
      };
      __pinTick();
      // Update cluster + auto-heat source data — sadece içerik gerçekten değiştiyse (sıralama değişikliği setData tetiklemesin)
      const idKey = filtered.map(p=>p.id).sort().join(',');
      if (lastSourceKeyRef.current !== idKey) {
        lastSourceKeyRef.current = idKey;
        try {
          const clSrc = map.getSource('places-src');
          if (clSrc) clSrc.setData({type:'FeatureCollection',features:filtered.map(p=>({type:'Feature',geometry:{type:'Point',coordinates:[p.lng,p.lat]},properties:{id:p.id,type:p.type}}))});
        } catch(e) {}
        try {
          const ahSrc = map.getSource('auto-heat-src');
          if (ahSrc) ahSrc.setData({type:'FeatureCollection',features:filtered.filter(p=>p.lat&&p.lng).map(p=>({type:'Feature',geometry:{type:'Point',coordinates:[p.lng,p.lat]},properties:{w:1}}))});
        } catch(e) {}
      }
      // Apply current zoom state to newly created markers
      if (applyZoomRef.current) applyZoomRef.current();
      // Drop tracking yukarıda pin başına yapılıyor (droppedPinsRef.add).
    };
    doMarkers();

    // Pan/zoom değişiminde marker setini güncelle — 300ms debounce
    const onMove = () => {
      if (moveDebounceTimer) clearTimeout(moveDebounceTimer);
      moveDebounceTimer = setTimeout(() => { if (!cancelled) doMarkers(); }, 300);
    };
    map.on('moveend', onMove);
    map.on('zoomend', onMove);

    return () => {
      cancelled = true;
      if (moveDebounceTimer) clearTimeout(moveDebounceTimer);
      try { map.off('moveend', onMove); map.off('zoomend', onMove); } catch (e) {}
    };
  }, [filtered, places, activeCategory, mode, mapLoaded, campaignsBySchool]);

  // ─── MARKER OPACITY (zone/category/heat) — runs on childAge change too ──────
  useEffect(() => {
    Object.keys(markersRef.current).filter(k => !k.startsWith('t_')).forEach(k => {
      const p = places.find(x => String(x.id) === String(k));
      if (!p) return;
      const el = markersRef.current[k].getElement();
      if (!el) return;
      if (clusterHidingRef.current) return;
      const catHide = activeCategory !== 'all' && p.type !== activeCategory;
      const ageMatch = childAge >= (p.ageMin||0) && childAge <= (p.ageMax||18);
      const zoneDim = mode === 'zone' && !ageMatch;
      el.style.display = mode === 'heat' ? 'none' : '';
      el.style.opacity = catHide ? '0.1' : zoneDim ? '0.18' : '1';
      el.style.pointerEvents = 'all';
    });
  }, [childAge, mode, activeCategory, places, mapLoaded]);

  // ─── PROVINCE LAYER (z<8 görünür) ──────────────────────────────────────────
  // Hibrit: turkey-provinces.geojson varsa gerçek choropleth (il sınırları
  // boyalı), yoksa bubble fallback (her il = Point centroid daire).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !places.length) return;

    const TR_NORM = (s) => (s || '').toLocaleLowerCase('tr').trim()
      .replace(/i̇/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
      .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c');

    const NAME_KEYS = ['name', 'NAME_1', 'name_tr', 'il', 'NAME', 'PROVINCE'];
    const pickName = (props) => {
      for (const k of NAME_KEYS) if (props && props[k]) return String(props[k]);
      return '';
    };

    const computeBBox = (geom) => {
      let mnLng = Infinity, mnLat = Infinity, mxLng = -Infinity, mxLat = -Infinity;
      const walk = (c) => {
        if (typeof c[0] === 'number') {
          if (c[0] < mnLng) mnLng = c[0]; if (c[0] > mxLng) mxLng = c[0];
          if (c[1] < mnLat) mnLat = c[1]; if (c[1] > mxLat) mxLat = c[1];
        } else for (const x of c) walk(x);
      };
      walk(geom.coordinates);
      return [[mnLng, mnLat], [mxLng, mxLat]];
    };

    const buildCounts = () => {
      const counts = {};
      for (const p of places) {
        const c = TR_NORM(p.city);
        if (c) counts[c] = (counts[c] || 0) + 1;
      }
      return counts;
    };

    // ── Bubble fallback (dosya yoksa)
    const buildBubbles = () => {
      const agg = {};
      for (const p of places) {
        if (!p.lat || !p.lng) continue;
        const city = (p.city || '').trim();
        if (!city) continue;
        if (!agg[city]) agg[city] = { sum_lat: 0, sum_lng: 0, count: 0, min_lat: 90, min_lng: 180, max_lat: -90, max_lng: -180 };
        const a = agg[city];
        a.sum_lat += p.lat; a.sum_lng += p.lng; a.count += 1;
        if (p.lat < a.min_lat) a.min_lat = p.lat;
        if (p.lat > a.max_lat) a.max_lat = p.lat;
        if (p.lng < a.min_lng) a.min_lng = p.lng;
        if (p.lng > a.max_lng) a.max_lng = p.lng;
      }
      return {
        type: 'FeatureCollection',
        features: Object.entries(agg).map(([city, a]) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [a.sum_lng / a.count, a.sum_lat / a.count] },
          properties: {
            name: city, count: a.count,
            min_lat: a.min_lat, min_lng: a.min_lng, max_lat: a.max_lat, max_lng: a.max_lng,
          },
        })),
      };
    };

    const enrichChoropleth = (raw, counts) => ({
      type: 'FeatureCollection',
      features: (raw.features || []).map(f => {
        const name = pickName(f.properties);
        const cnt = counts[TR_NORM(name)] || 0;
        return { ...f, properties: { ...f.properties, name, count: cnt } };
      }),
    });

    const ensureLayers = async () => {
      if (!map.isStyleLoaded()) { setTimeout(ensureLayers, 100); return; }
      const counts = buildCounts();

      // Choropleth source mevcutsa data güncelle
      const choroSrc = map.getSource('provinces');
      if (choroSrc && window.__provinceRaw) {
        choroSrc.setData(enrichChoropleth(window.__provinceRaw, counts));
        return;
      }
      // Bubble source mevcutsa data güncelle
      const bubbleSrc = map.getSource('province-bubbles');
      if (bubbleSrc) { bubbleSrc.setData(buildBubbles()); return; }

      // İlk yükleme — geojson dosyası dene, yoksa bubble fallback
      // Cached raw varsa fetch atla (theme switch'te sürekli fetch'in
      // önüne geçmek için). İlk yükleme veya cache yoksa fetch.
      let raw = window.__provinceRaw || null;
      if (!raw) {
        try {
          const res = await fetch('/turkey-provinces.geojson');
          if (res.ok) {
            const text = await res.text();
            if (text.length > 1000) raw = JSON.parse(text);
          }
        } catch {}
        if (raw) window.__provinceRaw = raw;
      }

      if (raw && Array.isArray(raw.features) && raw.features.length > 50) {
        // CHOROPLETH — gerçek il sınırları
        window.__provinceRaw = raw;
        map.addSource('provinces', { type: 'geojson', data: enrichChoropleth(raw, counts) });

        const before = map.getLayer('clusters') ? 'clusters' : undefined;
        // Light tema: koyu mor tonları (light map üzerinde okunaklı)
        // Dark tema: açık mor tonları (dark map üzerinde okunaklı)
        const fillColor = isDark
          ? ['step', ['get', 'count'],
              '#2a2a3a', 1, '#E9D5FF', 11, '#C4B5FD', 51, '#A78BFA', 201, '#7C3AED']
          : ['step', ['get', 'count'],
              'rgba(0,0,0,0)', 1, '#DDD6FE', 11, '#A78BFA', 51, '#7C3AED', 201, '#5B21B6'];
        const fillOpacity = isDark
          ? ['interpolate', ['linear'], ['zoom'], 4, 0.78, 7, 0.6, 8, 0]
          : ['interpolate', ['linear'], ['zoom'], 4, 0.55, 7, 0.45, 8, 0];
        const lineColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
        const textColor = isDark ? '#fff' : '#1E1048';
        const textHalo = isDark ? '#000' : '#fff';

        map.addLayer({
          id: 'province-fill', type: 'fill', source: 'provinces', maxzoom: 8,
          paint: {
            'fill-color': fillColor,
            'fill-opacity': fillOpacity,
          }
        }, before);
        map.addLayer({
          id: 'province-border', type: 'line', source: 'provinces', maxzoom: 8,
          paint: {
            'line-color': lineColor,
            'line-width': 1,
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 7, 0.5, 8, 0],
          }
        }, before);
        map.addLayer({
          id: 'province-label', type: 'symbol', source: 'provinces', maxzoom: 8,
          filter: ['>=', ['get', 'count'], 30],
          layout: {
            'text-field': ['concat', ['get', 'name'], ' · ', ['to-string', ['get', 'count']]],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 7, 13],
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': textColor,
            'text-halo-color': textHalo,
            'text-halo-width': 1.4,
            'text-opacity': ['interpolate', ['linear'], ['zoom'], 4, 1, 7, 0.9, 8, 0],
          }
        });

        map.on('click', 'province-fill', (e) => {
          const f = e.features && e.features[0]; if (!f) return;
          // O ile ait pinlerin drop set'ten çıkar — zoom sonrası
          // yeniden oluşturulduklarında drop animasyonu tekrar çalışsın.
          const cityName = f.properties?.name;
          if (cityName && droppedPinsRef.current) {
            const TR_NORM2 = (s) => (s||'').toLocaleLowerCase('tr').trim()
              .replace(/i̇/g,'i').replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ş/g,'s')
              .replace(/ı/g,'i').replace(/ö/g,'o').replace(/ç/g,'c');
            const target = TR_NORM2(cityName);
            for (const p of (placesRef.current || [])) {
              if (TR_NORM2(p.city) === target) droppedPinsRef.current.delete(String(p.id));
            }
          }
          try { map.fitBounds(computeBBox(f.geometry), { padding: 60, duration: 300, maxZoom: 10 }); } catch {}
        });
        map.on('mouseenter', 'province-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'province-fill', () => { map.getCanvas().style.cursor = ''; });
      } else {
        // BUBBLE FALLBACK
        if (!raw) console.warn('[province] turkey-provinces.geojson yok — bubble fallback');
        const fc = buildBubbles();
        map.addSource('province-bubbles', { type: 'geojson', data: fc });
        const before = map.getLayer('clusters') ? 'clusters' : undefined;
        const bubbleColor = isDark
          ? ['step', ['get', 'count'], '#E9D5FF', 11, '#C4B5FD', 51, '#A78BFA', 201, '#7C3AED']
          : ['step', ['get', 'count'], '#A78BFA', 11, '#7C3AED', 51, '#5B21B6', 201, '#4C1D95'];
        const bubbleStroke = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.85)';
        const bubbleTextColor = isDark ? '#fff' : '#1E1048';
        const bubbleHalo = isDark ? '#000' : '#fff';
        map.addLayer({
          id: 'province-bubble', type: 'circle', source: 'province-bubbles', maxzoom: 8,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['get', 'count'],
              1, 8, 10, 12, 50, 18, 200, 26, 1000, 36],
            'circle-color': bubbleColor,
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.85, 7, 0.7, 8, 0],
            'circle-stroke-color': bubbleStroke,
            'circle-stroke-width': 1.5,
          }
        }, before);
        map.addLayer({
          id: 'province-bubble-label', type: 'symbol', source: 'province-bubbles', maxzoom: 8,
          filter: ['>=', ['get', 'count'], 30],
          layout: {
            'text-field': ['concat', ['get', 'name'], ' · ', ['to-string', ['get', 'count']]],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 7, 12],
            'text-offset': [0, 1.6], 'text-anchor': 'top', 'text-allow-overlap': false,
          },
          paint: {
            'text-color': bubbleTextColor, 'text-halo-color': bubbleHalo, 'text-halo-width': 1.4,
            'text-opacity': ['interpolate', ['linear'], ['zoom'], 4, 1, 7, 0.9, 8, 0],
          }
        });
        map.on('click', 'province-bubble', (e) => {
          const f = e.features && e.features[0]; if (!f) return;
          const p = f.properties;
          // Drop set'ten o ile ait pinleri çıkar — drop animasyonu yeniden tetiklensin
          const cityName = p?.name;
          if (cityName && droppedPinsRef.current) {
            const TR_NORM2 = (s) => (s||'').toLocaleLowerCase('tr').trim()
              .replace(/i̇/g,'i').replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ş/g,'s')
              .replace(/ı/g,'i').replace(/ö/g,'o').replace(/ç/g,'c');
            const target = TR_NORM2(cityName);
            for (const sp of (placesRef.current || [])) {
              if (TR_NORM2(sp.city) === target) droppedPinsRef.current.delete(String(sp.id));
            }
          }
          try {
            map.fitBounds(
              [[Number(p.min_lng), Number(p.min_lat)], [Number(p.max_lng), Number(p.max_lat)]],
              { padding: 60, duration: 300, maxZoom: 10 }
            );
          } catch {}
        });
        map.on('mouseenter', 'province-bubble', () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'province-bubble', () => { map.getCanvas().style.cursor = ''; });
      }
    };
    ensureLayers();
  }, [places, mapLoaded, isDark, styleVersion]);

  // ─── EVENT MARKERS ──────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.mapboxgl || !mapLoaded) return;
    // Remove old event markers
    Object.keys(eventMarkersRef.current).forEach(k => {
      eventMarkersRef.current[k].remove();
      delete eventMarkersRef.current[k];
    });
    if (!showEvents) return;
    const EVT_ICONS = {at_binicilik:'🐴',yuzme:'🏊',bale:'🩰',satranc:'♟️',resim:'🎨',doga:'🌿',spor:'⚽',muzik:'🎵',diger:'📅'};
    const EVT_COLORS = {at_binicilik:'#8B4513',yuzme:'#0EA5E9',bale:'#EC4899',satranc:'#6366F1',resim:'#F59E0B',doga:'#22C55E',spor:'#EF4444',muzik:'#A855F7',diger:'#FF8C42'};
    eventsData.forEach((ev, i) => {
      if (!ev.lat || !ev.lng) return;
      const icon = EVT_ICONS[ev.type] || '📅';
      const color = EVT_COLORS[ev.type] || '#FF8C42';
      const el = document.createElement('div');
      el.style.cssText = 'cursor:pointer;overflow:visible;';
      el.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;animation:pinDropMB 0.5s cubic-bezier(0.34,1.5,0.64,1) '+(i*0.07)+'s both;">'
        +'<div style="width:38px;height:38px;background:#FF8C42;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2.5px solid rgba(255,255,255,0.8);box-shadow:0 4px 16px rgba(255,140,66,0.5);">'
        +'<span style="transform:rotate(45deg);font-size:18px;line-height:1;display:block;">'+icon+'</span>'
        +'</div>'
        +'<div style="width:5px;height:5px;background:#FF8C42;border-radius:50%;opacity:0.7;margin-top:-1px;"></div>'
        +'</div>';
      // Drop animasyonunu bir kez oynatıp kaldır
      setTimeout(function(){
        const inner = el.firstElementChild;
        if (inner && inner.style && inner.style.animation && inner.style.animation.indexOf('pinDropMB') >= 0) {
          inner.style.animation = 'none';
        }
      }, (i * 70) + 700);
      const dateStr = new Date(ev.date).toLocaleDateString('tr-TR',{weekday:'short',day:'numeric',month:'long'});
      const timeStr = ev.time_start ? (ev.time_start+(ev.time_end?' – '+ev.time_end:'')) : '';
      const priceStr = ev.price != null ? Number(ev.price).toLocaleString('tr-TR')+' ₺' : 'Ücretsiz';
      const phone = ev.contact_phone ? ev.contact_phone.replace(/\s/g,'') : '';
      const spotsLeft = ev.capacity ? Math.max(0, ev.capacity - (ev.enrolled_count||0)) : null;
      const spotsHtml = spotsLeft !== null
        ? '<div style="margin-top:10px;padding:6px 10px;background:rgba(255,255,255,0.06);border-radius:8px;display:flex;align-items:center;gap:8px;">'
          +'<div style="flex:1;height:5px;background:rgba(255,255,255,0.1);border-radius:3px;overflow:hidden;">'
          +'<div style="height:100%;width:'+Math.round(((ev.enrolled_count||0)/ev.capacity)*100)+'%;background:'+(spotsLeft<=3?'#EF4444':spotsLeft<=10?'#F59E0B':'#22C55E')+';border-radius:3px;"></div></div>'
          +'<span style="font-size:10px;color:'+(spotsLeft<=3?'#EF4444':spotsLeft<=10?'#F59E0B':'#22C55E')+';font-weight:700;white-space:nowrap;">'+spotsLeft+' yer kaldı</span>'
          +'</div>'
        : '';
      const contactHtml = phone
        ? '<div style="margin-top:10px;display:flex;gap:6px;">'
          +'<a href="https://wa.me/90'+phone.replace(/^0/,'')+'" target="_blank" style="flex:1;padding:10px 0;border-radius:10px;background:#25D366;border:none;color:#fff;font-size:13px;font-weight:700;cursor:pointer;text-align:center;text-decoration:none;font-family:DM Sans,sans-serif;">💬 WhatsApp</a>'
          +'<a href="tel:'+phone+'" style="flex:1;padding:10px 0;border-radius:10px;background:rgba(168,218,255,0.15);border:1px solid rgba(168,218,255,0.3);color:#A8DAFF;font-size:13px;font-weight:700;cursor:pointer;text-align:center;text-decoration:none;font-family:DM Sans,sans-serif;">📞 Ara</a>'
          +'</div>'
        : '';
      const popup = new window.mapboxgl.Popup({offset:[0,-35],anchor:'bottom',closeButton:true,closeOnClick:true,className:'ob-popup',maxWidth:'280px'}).setHTML(
        '<div style="font-family:DM Sans,sans-serif;overflow:hidden;border-radius:14px;">'
        +'<div style="background:linear-gradient(135deg,'+color+'ee,'+color+'99);padding:16px 18px;position:relative;">'
        +'<div style="font-size:32px;margin-bottom:6px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.2));">'+icon+'</div>'
        +'<div style="font-weight:800;font-size:16px;color:#fff;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.2);">'+escapeHTML(ev.title||'')+'</div>'
        +'<div style="position:absolute;top:12px;right:14px;background:rgba(0,0,0,0.3);backdrop-filter:blur(4px);border-radius:8px;padding:4px 10px;font-size:13px;font-weight:800;color:#fff;">'+priceStr+'</div>'
        +'</div>'
        +'<div style="padding:14px 18px;">'
        +'<div style="display:flex;flex-direction:column;gap:6px;margin-bottom:4px;">'
        +'<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:rgba(255,255,255,0.7);"><span style="font-size:14px;">📅</span> '+dateStr+'</div>'
        +(timeStr ? '<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:rgba(255,255,255,0.7);"><span style="font-size:14px;">🕐</span> '+timeStr+'</div>' : '')
        +(ev.capacity ? '<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:rgba(255,255,255,0.7);"><span style="font-size:14px;">👥</span> '+ev.capacity+' kişi kapasite</div>' : '')
        +'</div>'
        +spotsHtml
        +contactHtml
        +'<button onclick="window.__okulmap_reserveEvent(\''+escapeHTML(ev.id)+'\')" style="margin-top:10px;width:100%;padding:11px 0;border-radius:10px;background:linear-gradient(135deg,'+color+','+color+'cc);border:none;color:#fff;font-size:14px;font-weight:800;cursor:pointer;font-family:DM Sans,sans-serif;letter-spacing:0.3px;">🎟 Hemen Başvur</button>'
        +'</div>'
        +'</div>'
      );
      el.addEventListener('click', () => {
        map.flyTo({center:[ev.lng,ev.lat],zoom:15,duration:800});
        setTimeout(() => { const m = eventMarkersRef.current['ev_'+ev.id]; if(m&&!m.getPopup().isOpen()) m.togglePopup(); },900);
      });
      eventMarkersRef.current['ev_'+ev.id] = new window.mapboxgl.Marker({element:el}).setLngLat([ev.lng,ev.lat]).setPopup(popup).addTo(map);
    });
  }, [showEvents, eventsData, mapLoaded]);

  // ─── SORT fallback when userPos clears ──────────────────────────────────────
  useEffect(() => {
    if (!userPos && sortBy === 'distance') setSortBy('rating');
  }, [userPos]);

  // ─── MAP RESIZE on mobile tab switch ────────────────────────────────────────
  useEffect(() => {
    if (!isMobile || mobileTab !== 'map') return;
    const map = mapRef.current;
    if (!map) return;
    setTimeout(() => { try { map.resize(); } catch(e) {} }, 50);
  }, [mobileTab, isMobile]);

  // ─── UPDATE LAYERS ON MODE CHANGE ──────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const doUpdate = () => {
      if (!map.isStyleLoaded()) { map.once('idle', doUpdate); return; }

      if (mode === 'heat') {
        try {
          if (map.getLayer('heat-lyr')) map.removeLayer('heat-lyr');
          if (map.getSource('heat-src')) map.removeSource('heat-src');
          map.addSource('heat-src', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: placesRef.current.filter(p => p.lat && p.lng).map(p => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
                properties: { weight: p.enrolled_count || 1 }
              }))
            }
          });
          map.addLayer({
            id: 'heat-lyr',
            type: 'heatmap',
            source: 'heat-src',
            paint: {
              'heatmap-radius': 25,
              'heatmap-opacity': 1,
              'heatmap-intensity': 1.5,
              'heatmap-color': [
                'interpolate', ['linear'], ['heatmap-density'],
                0, 'rgba(0,255,0,0)',
                0.2, 'green',
                0.5, 'yellow',
                0.8, 'orange',
                1, 'red'
              ]
            }
          });
        } catch(e) { console.error('[HEAT] error:', e); }
      } else {
        try {
          if (map.getLayer('heat-lyr')) map.removeLayer('heat-lyr');
          if (map.getSource('heat-src')) map.removeSource('heat-src');
        } catch(e) {}
      }

      const show = (id, vis) => { try { map.setLayoutProperty(id,'visibility',vis?'visible':'none'); } catch(e) {} };
      ['moda','kalamis','yeldeg','hasan'].forEach(z => {
        show('safe-fill-'+z, mode==='safety');
        show('safe-line-'+z, mode==='safety');
      });
      // Heat overlay sadece mode==='heat' iken görünür (default kapalı —
      // choropleth bu rolü üstlendi). Eski davranış: zoom-out'ta her zaman
      // auto-heat görünüyordu, choropleth ile çakışıyordu.
      show('auto-heat-lyr', mode === 'heat');
    };
    doUpdate();
  }, [mode, mapLoaded]);

  // ─── RADIUS CIRCLE ON MAP ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const doRadius = () => {
      if (!map.isStyleLoaded()) { map.once('styledata', doRadius); return; }
      try { if(map.getLayer('radius-fill')) map.removeLayer('radius-fill'); } catch(e){}
      try { if(map.getLayer('radius-line')) map.removeLayer('radius-line'); } catch(e){}
      try { if(map.getSource('radius-src')) map.removeSource('radius-src'); } catch(e){}
      if (!radiusKm || !userPos) return;
      const _uLng = Array.isArray(userPos) ? userPos[0] : userPos.lng;
      const _uLat = Array.isArray(userPos) ? userPos[1] : userPos.lat;
      const circle = makeCircle(_uLng, _uLat, radiusKm);
      map.addSource('radius-src', {type:'geojson', data:circle});
      map.addLayer({id:'radius-fill', type:'fill', source:'radius-src', paint:{'fill-color':'#A8DAFF','fill-opacity':0.07}});
      map.addLayer({id:'radius-line', type:'line', source:'radius-src', paint:{'line-color':'#A8DAFF','line-width':1.5,'line-dasharray':[4,3],'line-opacity':0.6}});
    };
    doRadius();
  }, [radiusKm, userPos, mapLoaded]);

  // ─── UPDATE ROUTE ──────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const doRoute = () => {
      if (!map.isStyleLoaded()) { map.once('styledata', doRoute); return; }
      try { if(map.getLayer('route-label')) map.removeLayer('route-label'); } catch(e) {}
      try { if(map.getSource('route-label')) map.removeSource('route-label'); } catch(e) {}
      try { if(map.getLayer('route')) map.removeLayer('route'); } catch(e) {}
      try { if(map.getSource('route')) map.removeSource('route'); } catch(e) {}
      if (!routeId) return;
      const p = places.find(x => x.id === routeId);
      if (!p) return;
      const start = userPosRef.current || [29.0272, 40.9898];
      const dist = hav(start[1],start[0],p.lat,p.lng);
      const mid = [(start[0]+p.lng)/2, (start[1]+p.lat)/2];
      map.addSource('route',{type:'geojson',data:{type:'Feature',geometry:{type:'LineString',coordinates:[start,[p.lng,p.lat]]}}});
      map.addLayer({id:'route',type:'line',source:'route',paint:{'line-color':'#A8DAFF','line-width':3,'line-dasharray':[3,2],'line-opacity':0.85}});
      map.addSource('route-label',{type:'geojson',data:{type:'Feature',geometry:{type:'Point',coordinates:mid},properties:{label:fmtD(dist)}}});
      map.addLayer({id:'route-label',type:'symbol',source:'route-label',layout:{'text-field':['get','label'],'text-font':['DIN Offc Pro Medium','Arial Unicode MS Bold'],'text-size':13,'text-offset':[0,-1],'text-anchor':'bottom'},paint:{'text-color':'#A8DAFF','text-halo-color':'rgba(0,0,0,0.8)','text-halo-width':2}});
    };
    doRoute();
  }, [routeId, places, mapLoaded]);

  // ─── USER POSITION PULSE MARKER ────────────────────────────────────────────
  useEffect(() => {
    if (!userPos || !mapLoaded || !window.mapboxgl || !mapRef.current) return;
    if (userMarkerRef.current) { try { userMarkerRef.current.remove(); } catch(e) {} }
    const el = document.createElement('div');
    el.style.cssText = 'position:relative;width:14px;height:14px;pointer-events:none;';
    el.innerHTML = '<div style="position:absolute;inset:0;border-radius:50%;background:#A8DAFF;border:2px solid #fff;z-index:2;box-shadow:0 0 10px rgba(168,218,255,0.7);"></div>'
      +'<div style="position:absolute;top:50%;left:50%;width:40px;height:40px;border-radius:50%;background:rgba(168,218,255,0.2);transform:translate(-50%,-50%);animation:pulseRing2 1.8s ease-out infinite;"></div>'
      +'<div style="position:absolute;top:50%;left:50%;width:60px;height:60px;border-radius:50%;background:rgba(168,218,255,0.1);transform:translate(-50%,-50%);animation:pulseRing2 1.8s ease-out 0.7s infinite;"></div>';
    const lngLat = Array.isArray(userPos) ? userPos : [userPos.lng, userPos.lat];
    userMarkerRef.current = new window.mapboxgl.Marker({element:el, anchor:'center'})
      .setLngLat(lngLat)
      .addTo(mapRef.current);
    return () => { if (userMarkerRef.current) { try { userMarkerRef.current.remove(); } catch(e) {} userMarkerRef.current = null; } };
  }, [userPos, mapLoaded]);

  // ─── SELECTED PIN PULSE RING ────────────────────────────────────────────────
  useEffect(() => {
    Object.keys(markersRef.current).filter(k => !k.startsWith('t_')).forEach(k => {
      const el = markersRef.current[k]?.getElement();
      if (!el) return;
      const existing = el.querySelector('.sel-pulse');
      const isSel = String(k) === String(selectedId);
      if (isSel && !existing) {
        const ring = document.createElement('div');
        ring.className = 'sel-pulse';
        ring.style.cssText = 'position:absolute;top:35%;left:50%;width:52px;height:52px;border-radius:50%;border:2px solid #A8DAFF;animation:pulseRing 1.4s ease-out infinite;pointer-events:none;z-index:-1;';
        el.style.isolation = 'isolate';
        el.appendChild(ring);
      } else if (!isSel && existing) {
        existing.remove();
      }
      // Clean up any legacy position:relative left over from previous sessions — Mapbox needs absolute
      if (el.style.position === 'relative') el.style.position = '';
    });
  }, [selectedId]);

  // ─── THEME CHANGE ON MAP ───────────────────────────────────────────────────
  // setStyle TÜM custom source/layer'ları temizler. Yeni style yüklenince
  // yeniden eklemek şart — yoksa choropleth ve heat layer kayboluyor.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    try {
      map.setStyle(isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12');
      map.once('style.load', () => {
        map.getStyle().layers.forEach(l => {
          if (l.id.includes('road') && l.type === 'symbol') map.setLayoutProperty(l.id, 'visibility', 'none');
        });
        // Custom layer'ları yeniden ekle
        try { addBaseLayers(map); } catch (e) { console.warn('[theme] addBaseLayers re-add hatası', e); }
        // Province useEffect'i yeniden tetikle — fresh re-add path
        // (eski source wiped, yeni colors ile eklenir)
        setStyleVersion(v => v + 1);
      });
    } catch(e) {}
  }, [isDark, mapLoaded]);

  // ─── FAV TOGGLE ─────────────────────────────────────────────────────────────
  const toggleFav = useCallback((placeId) => {
    if (!user) { setShowAuth(true); setAuthShowEmail(true); return; }
    const isFav = favs.includes(placeId);
    if (isFav) {
      haptic(8);
      setFavs(prev => prev.filter(id => id !== placeId));
      supabase.from('favorites').delete().eq('user_id', user.id).eq('school_id', placeId);
      showToast('Favorilerden kaldırıldı');
    } else {
      haptic([8, 30, 8]);
      setFavs(prev => [...prev, placeId]);
      supabase.from('favorites').insert({user_id: user.id, school_id: placeId});
      trackAnalytics(placeId, 'favorite');
      showToast('❤️ Favorilere eklendi');
    }
  }, [user, favs, showToast]);

  // ─── COMPARE TOGGLE ─────────────────────────────────────────────────────────
  const handleCompare = useCallback((id) => {
    setCompareList(prev => {
      if (prev.includes(id)) { haptic(8); return prev.filter(x=>x!==id); }
      if (prev.length<3) { haptic([8, 30, 8]); trackAnalytics(id, 'compare'); return [...prev,id]; }
      haptic([20, 50, 20]); // limit aşıldı — uyarı titreşimi
      return prev;
    });
  }, [trackAnalytics]);

  // ─── DETAIL MODAL ──────────────────────────────────────────────────────────
  // DetailModal is defined at module level as React.memo — see above OkulMap


  // ─── NOTIFICATIONS DRAWER ────────────────────────────────────────────────────
  function NotificationsDropdown() {
    const unread = notifications.filter(n=>!n.read).length;
    const markRead = (id) => setNotifications(prev=>prev.map(n=>n.id===id?{...n,read:true}:n));
    return (
      <>
        {/* backdrop — şeffaf, dışarı tıklayınca kapatır */}
        <div style={{position:'fixed',inset:0,zIndex:599}} onClick={()=>setShowNotif(false)}/>
        {/* dropdown panel */}
        <div style={{position:'absolute',top:'calc(100% + 10px)',right:0,width:360,maxHeight:480,overflowY:'auto',background:C('surf'),borderRadius:16,border:'1px solid '+C('bord'),boxShadow:'0 16px 48px rgba(0,0,0,0.45)',zIndex:600,animation:'fadeIn 0.15s ease'}}>
          {/* Header */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 16px 12px',borderBottom:'1px solid '+C('bord'),position:'sticky',top:0,background:C('surf'),zIndex:1}}>
            <div>
              <div style={{color:C('txt'),fontSize:14,fontWeight:700}}>Bildirimler</div>
              {unread>0 && <div style={{color:C('txt2'),fontSize:11,marginTop:1}}>{unread} okunmamış</div>}
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              {unread>0 && (
                <button onClick={()=>setNotifications(prev=>prev.map(n=>({...n,read:true})))}
                  style={{background:'none',border:'none',color:'#A8DAFF',fontSize:11,cursor:'pointer',fontFamily:'DM Sans,sans-serif',fontWeight:600}}>
                  Tümünü okundu işaretle
                </button>
              )}
              <button onClick={()=>setShowNotif(false)}
                style={{width:26,height:26,borderRadius:7,background:C('surf2'),border:'1px solid '+C('bord'),color:C('txt2'),cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
            </div>
          </div>
          {/* Items */}
          <div style={{padding:'8px 10px 10px'}}>
            {notifications.map(n=>(
              <div key={n.id} onClick={()=>markRead(n.id)}
                style={{display:'flex',gap:10,padding:'10px 10px',borderRadius:10,background:n.read?'transparent':C('surf2'),border:'1px solid '+(n.read?'transparent':n.color+'25'),marginBottom:4,cursor:'pointer',transition:'background 0.15s'}}>
                <div style={{width:36,height:36,borderRadius:9,background:n.color+'18',border:'1px solid '+n.color+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>{n.icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:2}}>
                    <span style={{color:n.read?C('txt2'):C('txt'),fontSize:12,fontWeight:n.read?500:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{n.title}</span>
                    {!n.read && <div style={{width:6,height:6,borderRadius:'50%',background:n.color,flexShrink:0,marginLeft:6,boxShadow:'0 0 5px '+n.color}}/>}
                  </div>
                  <div style={{color:C('txt2'),fontSize:11,lineHeight:1.45,marginBottom:2}}>{n.body}</div>
                  <div style={{color:C('txt3'),fontSize:10}}>{n.time}</div>
                </div>
              </div>
            ))}
            {notifications.length===0 && (
              <div style={{textAlign:'center',padding:'32px 0',color:C('txt2'),fontSize:13}}>
                <Maskot type="mod" name="surprised" size={56} style={{margin:'0 auto 8px'}}/>
                Henüz bildirim yok
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ─── RECENT DROPDOWN ────────────────────────────────────────────────────────
  function RecentDropdown() {
    const recents = recentIds.map(id => places.find(p => String(p.id) === String(id))).filter(Boolean);
    return (
      <>
        <div style={{position:'fixed',inset:0,zIndex:599}} onClick={()=>setShowRecent(false)}/>
        <div style={{position:'absolute',top:'calc(100% + 10px)',right:0,width:340,maxHeight:480,overflowY:'auto',background:C('surf'),borderRadius:16,border:'1px solid '+C('bord'),boxShadow:'0 16px 48px rgba(0,0,0,0.45)',zIndex:600,animation:'fadeIn 0.15s ease'}}>
          {/* Header */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 16px 12px',borderBottom:'1px solid '+C('bord'),position:'sticky',top:0,background:C('surf'),zIndex:1}}>
            <div>
              <div style={{color:C('txt'),fontSize:14,fontWeight:700}}>Son Gezilenenler</div>
              {recents.length>0&&<div style={{color:C('txt2'),fontSize:11,marginTop:1}}>{recents.length} okul</div>}
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              {recents.length>0&&(
                <button onClick={()=>{ setRecentIds([]); try{localStorage.removeItem('okulmap_recent');}catch(e){} }}
                  style={{background:'none',border:'none',color:'rgba(255,107,107,0.7)',fontSize:11,cursor:'pointer',fontFamily:'DM Sans,sans-serif',fontWeight:600}}>
                  Temizle
                </button>
              )}
              <button onClick={()=>setShowRecent(false)}
                style={{width:26,height:26,borderRadius:7,background:C('surf2'),border:'1px solid '+C('bord'),color:C('txt2'),cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
            </div>
          </div>
          {/* Items */}
          <div style={{padding:'8px 10px 10px'}}>
            {recents.length===0 ? (
              <div style={{textAlign:'center',padding:'32px 0',color:C('txt2'),fontSize:13}}>
                <Maskot type="mod" name="merhaba" size={56} style={{margin:'0 auto 8px'}}/>
                Henüz okul gezmedin
              </div>
            ) : recents.map((p,idx)=>{
              const col = TC[p.type]||'#A8DAFF';
              const cat = CATS.find(c=>c.id===p.type)||{};
              const fillPct = Math.round((p.capacity>0?p.filled/p.capacity:0)*100);
              const fillColor = fillPct>80?'#FF6B6B':fillPct>60?'#FFE66D':'#4ECDC4';
              return (
                <div key={p.id} onClick={()=>{ setDetailId(p.id); setShowRecent(false); }}
                  style={{display:'flex',gap:10,padding:'10px 10px',borderRadius:10,border:'1px solid '+C('bord'),marginBottom:6,cursor:'pointer',transition:'background 0.15s'}}
                  className="card-hover">
                  <div style={{width:38,height:38,borderRadius:9,background:col+'18',border:'1px solid '+col+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,flexShrink:0}}>{cat.icon||'🏫'}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:2}}>
                      <span style={{color:C('txt'),fontSize:12,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</span>
                      <span style={{color:p.priceNum>0?col:C('txt3'),fontSize:10,fontWeight:p.priceNum>0?700:500,flexShrink:0,marginLeft:6}}>{p.price}</span>
                    </div>
                    <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4}}>
                      <span style={{color:'#FFE66D',fontSize:10}}>★ {p.rating}</span>
                      <span style={{color:C('txt2'),fontSize:10}}>{p.age} yaş</span>
                      <span style={{color:p.open?'#4ECDC4':'#888',fontSize:10}}>{p.open?'Açık':'Kapalı'}</span>
                    </div>
                    {p.open&&p.capacity>0&&(
                      <div style={{height:3,background:'rgba(255,255,255,0.06)',borderRadius:2,overflow:'hidden'}}>
                        <div style={{height:'100%',width:fillPct+'%',background:fillColor,borderRadius:2}}/>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  // ─── NAV ────────────────────────────────────────────────────────────────────
  function Nav() {
    const userName = user ? (user.name || user.email || '?') : '';
    return (
      <nav id="main-nav" style={{position:'sticky',top:0,zIndex:500,background:C('nav'),backdropFilter:'blur(20px)',borderBottom:'1px solid '+C('bord'),padding:'0 28px',flexShrink:0}}>
        <div className="nav-inner" style={{height:62,display:'flex',alignItems:'center',gap:16}}>
          {/* Logo — sol köşe */}
          <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0,cursor:'pointer'}}>
            <img src="/logo.png?v=3" alt="OkulMap" style={{height:86,objectFit:'contain'}} />
            <span className="nav-logo" style={{fontFamily:'Syne,sans-serif',fontSize:18,fontWeight:800,color:C('txt')}}>okul<span style={{color:'#A8DAFF'}}>map</span><span style={{fontSize:11,color:C('txt3'),fontWeight:400,marginLeft:2}}>.com</span></span>
          </div>

          <div style={{flex:1}}/>

          <button onClick={()=>setShowAddSchool(true)}
            title="Okul / Kurum Ekle"
            style={{padding:'6px 11px',borderRadius:9,fontSize:12,background:'rgba(168,218,255,0.10)',border:'1px solid rgba(168,218,255,0.35)',color:'#A8DAFF',cursor:'pointer',fontWeight:600,fontFamily:'DM Sans,sans-serif',transition:'all 0.2s',display:'flex',alignItems:'center',gap:6}}>
            <Building01Icon size={15} color="#A8DAFF" />
            Okul/Kurum Ekle
          </button>

          <button onClick={()=>setShowSupport(true)}
            title="Bize Ulaşın"
            style={{padding:'6px 11px',borderRadius:9,fontSize:12,background:'rgba(78,205,196,0.10)',border:'1px solid rgba(78,205,196,0.35)',color:'#4ECDC4',cursor:'pointer',fontWeight:600,fontFamily:'DM Sans,sans-serif',transition:'all 0.2s',display:'flex',alignItems:'center',gap:6}}>
            <CustomerSupportIcon size={15} color="#4ECDC4" />
            Bize Ulaşın
          </button>

          <button onClick={()=>setShowEvents(v=>!v)}
            style={{padding:'6px 11px',borderRadius:9,fontSize:12,background:showEvents?'rgba(255,140,66,0.2)':'rgba(255,255,255,0.04)',border:'1px solid '+(showEvents?'rgba(255,140,66,0.5)':C('bord')),color:showEvents?'#FF8C42':C('txt2'),cursor:'pointer',fontWeight:showEvents?700:500,fontFamily:'DM Sans,sans-serif',transition:'all 0.2s',display:'flex',alignItems:'center',gap:6}}>
            <Calendar01Icon size={15} color={showEvents?'#FF8C42':C('txt2')} />
            Etkinlikler{eventsData.length>0?' ('+eventsData.length+')':''}
          </button>

          <div className="nav-sep" style={{width:1,height:22,background:C('bord'),flexShrink:0}}/>

          {/* Icon buttons */}
          {(()=>{
            const unreadCount = notifications.filter(n=>!n.read).length;
            const favCount = favs.length;
            const cmpCount = compareList.length;
            const requireLogin = (fn) => () => { if(!user){setShowAuth(true);setAuthShowEmail(true);}else{fn();} };
            const iconBtn = (onClick, IconCmp, iconColor, badge, title, badgeColor) => (
              <button key={title} onClick={onClick} title={title}
                style={{width:36,height:36,borderRadius:9,background:'rgba(255,255,255,0.04)',border:'1px solid '+C('bord'),cursor:'pointer',position:'relative',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <IconCmp size={18} color={iconColor || C('txt2')} />
                {badge>0 && <div style={{position:'absolute',top:-4,right:-4,minWidth:15,height:15,borderRadius:'50%',background:badgeColor||'#FF6B6B',fontSize:8,fontWeight:800,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',padding:'0 3px'}}>{badge}</div>}
              </button>
            );
            return (
              <div style={{display:'flex',gap:6}}>
                {/* Son Gezilenenler */}
                <div style={{position:'relative'}}>
                  <button onClick={()=>{ setShowRecent(v=>!v); setShowNotif(false); }} title="Son Gezilenenler"
                    style={{width:36,height:36,borderRadius:9,background:showRecent?C('surf2'):'rgba(255,255,255,0.04)',border:'1px solid '+(showRecent?C('bord2'):C('bord')),cursor:'pointer',position:'relative',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <Clock01Icon size={18} color={showRecent?'#A8DAFF':C('txt2')} />
                    {recentIds.length>0&&<div style={{position:'absolute',top:-4,right:-4,minWidth:15,height:15,borderRadius:'50%',background:'#A8DAFF',fontSize:8,fontWeight:800,color:'#000',display:'flex',alignItems:'center',justifyContent:'center',padding:'0 3px'}}>{recentIds.length}</div>}
                  </button>
                  {showRecent && RecentDropdown()}
                </div>
                {/* Bildirimler */}
                <div style={{position:'relative'}}>
                  <button onClick={requireLogin(()=>setShowNotif(v=>!v))} title="Bildirimler"
                    style={{width:36,height:36,borderRadius:9,background:showNotif?C('surf2'):'rgba(255,255,255,0.04)',border:'1px solid '+(showNotif?C('bord2'):C('bord')),cursor:'pointer',position:'relative',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <Notification01Icon size={18} color={showNotif?'#A8DAFF':C('txt2')} />
                    {unreadCount>0 && <div style={{position:'absolute',top:-4,right:-4,minWidth:15,height:15,borderRadius:'50%',background:'#FF6B6B',fontSize:8,fontWeight:800,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',padding:'0 3px'}}>{unreadCount}</div>}
                  </button>
                  {showNotif && NotificationsDropdown()}
                </div>
                {iconBtn(requireLogin(()=>setLeftPanelView(v=>v==='favs'?'list':'favs')), FavouriteIcon, favCount>0?'#FF6B6B':C('txt2'), favCount, 'Favoriler', '#FF6B6B')}
                {iconBtn(()=>setShowCompare(true), BalanceScaleIcon, cmpCount>0?'#A8DAFF':C('txt2'), cmpCount, 'Karşılaştır', '#A8DAFF')}
              </div>
            );
          })()}

          <div className="nav-sep" style={{width:1,height:22,background:C('bord'),flexShrink:0}}/>

          {/* Theme */}
          <button onClick={function(){setTheme(function(t){return t==='dark'?'light':'dark';});}}
            style={{width:36,height:36,borderRadius:9,background:isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.05)',border:'1px solid '+C('bord'),cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <HugeiconsIcon icon={isDark?Sun02FreeIcons:Moon02FreeIcons} size={16} color={isDark?'#FFE66D':'#6366f1'} />
          </button>

          {/* Auth */}
          {user ? (() => {
            const role = user?.user_metadata?.role || (typeof window!=='undefined' && localStorage.getItem('okulmap_user_role')) || null;
            return (
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{width:34,height:34,borderRadius:'50%',background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'#000',cursor:'pointer',overflow:'hidden'}} title={role ? `Rol: ${role}` : ''}>
                {role ? <Maskot type="rol" name={role} size={30}/> : (userName[0] ? userName[0].toUpperCase() : '?')}
              </div>
              <span style={{color:C('txt2'),fontSize:13}}>{userName}</span>
              {staffInfo && (
                <a href={staffInfo.url} title="Yönetim paneline git"
                  style={{padding:'5px 10px',borderRadius:7,background:'#1E1048',color:'#fff',fontSize:11,fontWeight:700,textDecoration:'none',whiteSpace:'nowrap'}}>
                  🏫 Panele git
                </a>
              )}
              <button onClick={function(){
                localStorage.setItem('okulmap_signed_out','true');
                supabase.auth.signOut({scope:'global'}).then(function(){
                  Object.keys(localStorage).forEach(function(k){ if(k.startsWith('sb-')) localStorage.removeItem(k); });
                  localStorage.removeItem('okulmap_favs');
                  setUser(null);
                });
              }}
                style={{padding:'5px 12px',borderRadius:7,background:'rgba(255,100,100,0.1)',border:'1px solid rgba(255,100,100,0.25)',color:'#FF6B6B',fontSize:11,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                Çıkış
              </button>
            </div>
            );
          })() : (
            <button onClick={function(){setShowAuth(true);setAuthShowEmail(true);}} className="btn"
              style={{padding:'8px 20px',borderRadius:9,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',border:'none',color:'#000',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',boxShadow:'0 3px 10px rgba(168,218,255,0.3)'}}>
              Giriş Yap
            </button>
          )}
        </div>
      </nav>
    );
  }

  // ─── SAFETY PANEL ───────────────────────────────────────────────────────────
  function SafetyPanel() {
    return (
      <div style={{padding:'14px',borderBottom:'1px solid '+C('bord'),flexShrink:0,background:'rgba(123,228,149,0.04)',animation:'fadeIn 0.3s ease'}}>
        <div style={{color:'#7BE495',fontSize:11,fontWeight:700,marginBottom:10}}>🛡 Güvenlik Modu</div>
        <div style={{background:'rgba(255,255,255,0.03)',borderRadius:12,padding:'16px',border:'1px solid rgba(123,228,149,0.15)',textAlign:'center'}}>
          <div style={{fontSize:28,marginBottom:8}}>🔒</div>
          <div style={{color:C('txt'),fontSize:13,fontWeight:700,marginBottom:6}}>Güvenlik verisi yakında</div>
          <div style={{color:C('txt3'),fontSize:11,lineHeight:1.6}}>Mahalle güvenlik skorları ve resmi suç istatistikleri entegrasyonu üzerinde çalışıyoruz.</div>
        </div>
      </div>
    );
  }

  // ZonePanel artık inline JSX — aşağıda map panel içinde doğrudan render edilir

  // ─── MOBILE MODE BAR ────────────────────────────────────────────────────────
  function ModeBar() {
    return (
      <div className="mobile-mode-bar" style={{display:'none',gap:4,padding:'8px 12px',overflowX:'auto',borderBottom:'1px solid '+C('bord'),flexShrink:0,background:C('bg'),scrollbarWidth:'none'}}>
        {MODES.map(function(m){
          var isAct = mode === m.id;
          var mc = m.id==='heat'?'#FF6B6B':m.id==='transit'?'#C77DFF':m.id==='safety'?'#7BE495':'#A8DAFF';
          return (
            <button key={m.id} onClick={function(){setMode(m.id);}}
              style={{padding:'5px 11px',borderRadius:7,fontSize:11,background:isAct?mc+'20':'rgba(255,255,255,0.04)',border:'1px solid '+(isAct?mc+'50':C('bord')),color:isAct?mc:C('txt3'),cursor:'pointer',whiteSpace:'nowrap',fontFamily:'DM Sans,sans-serif'}}>
              {m.label}
            </button>
          );
        })}
      </div>
    );
  }

  // ─── LEFT PANEL ─────────────────────────────────────────────────────────────
  function LeftPanel() {
    const favPlaces = places.filter(p => favs.includes(p.id));
    return (
      <div id="left-panel" style={{width:380,flexShrink:0,display:'flex',flexDirection:'column',borderRight:'1px solid '+C('bord'),overflow:'hidden',background:C('bg')}}>
        {/* View toggle */}
        <div style={{display:'flex',borderBottom:'1px solid '+C('bord'),flexShrink:0}}>
          {[{id:'list',label:'🏫 Okullar'},{id:'favs',label:'❤️ Favorilerim'}].map(v=>(
            <button key={v.id} onClick={()=>setLeftPanelView(v.id)}
              style={{flex:1,padding:'10px 0',background:'none',border:'none',borderBottom:'2px solid '+(leftPanelView===v.id?'#A8DAFF':'transparent'),color:leftPanelView===v.id?'#A8DAFF':C('txt2'),fontSize:12,fontWeight:leftPanelView===v.id?700:400,cursor:'pointer',fontFamily:'DM Sans,sans-serif',transition:'all 0.15s'}}>
              {v.label}{v.id==='favs'&&favPlaces.length>0?' ('+favPlaces.length+')':''}
            </button>
          ))}
        </div>
        {leftPanelView==='list' ? (
          <>
            {/* Bu Hafta Etkinlikler */}
            {eventsData.length > 0 && (()=>{
              const EVT_ICONS = {at_binicilik:'🐴',yuzme:'🏊',bale:'🩰',satranc:'♟️',resim:'🎨',doga:'🌿',spor:'⚽',muzik:'🎵',diger:'📅'};
              const closest = eventsData.slice(0,5);
              return (
                <div style={{padding:'10px 12px',borderBottom:'1px solid '+C('bord'),flexShrink:0}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                    <span style={{fontSize:11,fontWeight:700,color:'#FF8C42',letterSpacing:'0.03em'}}>📅 Bu Hafta Etkinlikler</span>
                    <button onClick={()=>setShowEvents(v=>!v)} style={{fontSize:10,color:showEvents?'#FF8C42':C('txt3'),background:'none',border:'none',cursor:'pointer',fontFamily:'DM Sans,sans-serif',fontWeight:600}}>
                      {showEvents?'Gizle':'Haritada Gör'}
                    </button>
                  </div>
                  <div style={{display:'flex',gap:8,overflowX:'auto',paddingBottom:4}}>
                    {closest.map(ev=>{
                      const icon = EVT_ICONS[ev.type]||'📅';
                      const d = new Date(ev.date);
                      const day = d.getDate();
                      const mon = d.toLocaleDateString('tr-TR',{month:'short'});
                      return (
                        <div key={ev.id} onClick={()=>{
                          if(ev.lat&&ev.lng&&mapRef.current){setShowEvents(true);mapRef.current.flyTo({center:[ev.lng,ev.lat],zoom:15,duration:800});}
                        }} style={{minWidth:120,padding:'8px 10px',borderRadius:10,background:'rgba(255,140,66,0.08)',border:'1px solid rgba(255,140,66,0.2)',cursor:'pointer',flexShrink:0}}>
                          <div style={{fontSize:10,color:'#FF8C42',fontWeight:700,marginBottom:2}}>{day} {mon}</div>
                          <div style={{fontSize:11,color:C('txt'),fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{icon} {ev.title}</div>
                          {ev.time_start && <div style={{fontSize:9,color:C('txt3'),marginTop:2}}>🕐 {ev.time_start}</div>}
                          <div style={{fontSize:9,color:'#FF8C42',marginTop:2}}>{ev.price!=null?Number(ev.price).toLocaleString('tr-TR')+' ₺':'Ücretsiz'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            {mode === 'safety'  && SafetyPanel()}
            <Filters
              theme={theme} MAPBOX_TOKEN={MAPBOX_TOKEN}
              search={search} setSearch={setSearch}
              userPos={userPos} setUserPos={setUserPos} setLocLabel={setLocLabel}
              mapRef={mapRef} showToast={showToast}
              selectedIl={selectedIl} setSelectedIl={setSelectedIl}
              selectedIlce={selectedIlce} setSelectedIlce={setSelectedIlce}
              ilDropdownOpen={ilDropdownOpen} setIlDropdownOpen={setIlDropdownOpen}
              ilceDropdownOpen={ilceDropdownOpen} setIlceDropdownOpen={setIlceDropdownOpen}
              ilQuery={ilQuery} setIlQuery={setIlQuery}
              ilceQuery={ilceQuery} setIlceQuery={setIlceQuery}
              ilBtnRef={ilBtnRef} ilceBtnRef={ilceBtnRef} ilceCenterRef={ilceCenterRef}
              selectedMahalleler={selectedMahalleler} setSelectedMahalleler={setSelectedMahalleler}
              mahalleQuery={mahalleQuery} setMahalleQuery={setMahalleQuery}
              mahalleResults={mahalleResults} mahalleSearching={mahalleSearching}
              mahallePanelOpen={mahallePanelOpen} setMahallePanelOpen={setMahallePanelOpen}
              places={places} displayFiltered={displayFiltered}
              activeCategory={activeCategory} setActiveCategory={setActiveCategory}
              selectedAgeGroup={selectedAgeGroup} setSelectedAgeGroup={setSelectedAgeGroup}
              priceRange={priceRange} setPriceRange={setPriceRange}
              sortBy={sortBy} setSortBy={setSortBy}
              listView={listView} setListView={setListView}
            />
            <div id="place-list" style={{flex:1,overflowY:'auto',padding:'10px 12px'}}>
              {!loading && (
                <button onClick={()=>setShowPrefsModal(true)}
                  style={{width:'100%',padding:'10px 12px',marginBottom:10,borderRadius:10,background:userPrefs?'rgba(168,218,255,0.1)':'rgba(168,218,255,0.06)',border:'1px '+(userPrefs?'solid':'dashed')+' rgba(168,218,255,0.4)',color:'#A8DAFF',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                  🎯 {userPrefs ? 'Tercihimi düzenle' : 'Sana özel uygunluk skoru için tercihini söyle'}
                </button>
              )}
              {loading ? Array.from({length:6}).map(function(_,i){return <Skeleton key={i}/>;}) : ListItems()}
            </div>
          </>
        ) : (
          <div style={{flex:1,overflowY:'auto',padding:'10px 12px'}}>
            {!user ? (
              <div>
                <div style={{textAlign:'center',padding:'32px 20px 20px'}}>
                  <div style={{fontSize:36,marginBottom:12}}>❤️</div>
                  <div style={{color:C('txt'),fontSize:14,fontWeight:700,marginBottom:8}}>Favorilerim</div>
                  <div style={{color:C('txt2'),fontSize:12,marginBottom:16,lineHeight:1.6}}>Favori kurumlarını kaydetmek için giriş yapmalısın.</div>
                  <button onClick={()=>{setShowAuth(true);setAuthShowEmail(true);}} style={{padding:'10px 24px',borderRadius:10,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',border:'none',color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Giriş Yap</button>
                </div>
                {(()=>{
                  const recents=recentIds.map(id=>places.find(p=>String(p.id)===String(id))).filter(Boolean);
                  if(!recents.length) return null;
                  return (
                    <div style={{padding:'0 4px'}}>
                      <div style={{fontSize:10,fontWeight:600,color:C('txt3'),letterSpacing:'0.08em',marginBottom:8}}>SON GEZİLENLER</div>
                      {recents.map((p,idx)=>{ const col=TC[p.type]||'#A8DAFF'; const cat=CATS.find(c=>c.id===p.type)||{}; return (
                        <div key={p.id} className="card-hover" onClick={()=>setDetailId(p.id)}
                          style={{display:'flex',alignItems:'center',gap:10,padding:'9px 10px',borderRadius:10,border:'1px solid '+C('bord'),marginBottom:6,cursor:'pointer',animation:'slideUp 0.25s ease '+(Math.min(idx,20)*0.01)+'s both'}}>
                          <div style={{width:34,height:34,borderRadius:8,background:col+'18',border:'1px solid '+col+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>{cat.icon||'🏫'}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{color:C('txt'),fontSize:11,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                            <div style={{color:C('txt2'),fontSize:10}}>★ {p.rating} · {p.price}</div>
                          </div>
                        </div>
                      ); })}
                    </div>
                  );
                })()}
              </div>
            ) : favPlaces.length===0 ? (
              <div>
                <div style={{textAlign:'center',padding:'32px 20px 20px'}}>
                  <Maskot type="mod" name="sad" size={64} style={{margin:'0 auto 10px'}}/>
                  <div style={{color:C('txt2'),fontSize:13}}>Henüz favori eklemedin.</div>
                  <div style={{color:C('txt3'),fontSize:11,marginTop:4}}>Okul detayındaki ❤️ butonuna bas.</div>
                </div>
                {recentIds.length>0&&(()=>{
                  const recents=recentIds.map(id=>places.find(p=>String(p.id)===String(id))).filter(Boolean);
                  if(!recents.length) return null;
                  return (
                    <div style={{padding:'0 4px'}}>
                      <div style={{fontSize:10,fontWeight:600,color:C('txt3'),letterSpacing:'0.08em',marginBottom:8}}>SON GEZİLENLER</div>
                      {recents.map((p,idx)=>{ const col=TC[p.type]||'#A8DAFF'; const cat=CATS.find(c=>c.id===p.type)||{}; return (
                        <div key={p.id} className="card-hover" onClick={()=>setDetailId(p.id)}
                          style={{display:'flex',alignItems:'center',gap:10,padding:'9px 10px',borderRadius:10,border:'1px solid '+C('bord'),marginBottom:6,cursor:'pointer',animation:'slideUp 0.25s ease '+(Math.min(idx,20)*0.01)+'s both'}}>
                          <div style={{width:34,height:34,borderRadius:8,background:col+'18',border:'1px solid '+col+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>{cat.icon||'🏫'}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{color:C('txt'),fontSize:11,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                            <div style={{color:C('txt2'),fontSize:10}}>★ {p.rating} · {p.price}</div>
                          </div>
                        </div>
                      ); })}
                    </div>
                  );
                })()}
              </div>
            ) : favPlaces.map((p,idx)=>{
              const color = TC[p.type]||'#A8DAFF';
              const cat = CATS.find(c=>c.id===p.type)||{};
              return (
                <div key={p.id} className="card-hover"
                  style={{background:'transparent',border:'1px solid '+C('bord'),borderRadius:12,padding:'11px 13px',marginBottom:8,cursor:'pointer',display:'flex',alignItems:'center',gap:10,animation: (favPlaces.length > 50 ? 'fadeIn 0.3s ease both' : 'slideUp 0.3s ease '+(Math.min(idx,20)*0.01)+'s both')}}
                  onClick={()=>setDetailId(p.id)}>
                  <div style={{width:42,height:42,borderRadius:10,flexShrink:0,background:color+'18',border:'1px solid '+color+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>{cat.icon||'🏫'}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:C('txt'),fontSize:12,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                    <div style={{color:C('txt2'),fontSize:10,marginTop:2}}>★ {p.rating} · {p.age} yaş · {p.price}</div>
                  </div>
                  <button onClick={e=>{e.stopPropagation();toggleFav(p.id);}}
                    style={{width:32,height:32,borderRadius:8,background:'rgba(255,107,107,0.12)',border:'1px solid rgba(255,107,107,0.3)',color:'#FF6B6B',fontSize:14,cursor:'pointer',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    ❤️
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── SKELETON ───────────────────────────────────────────────────────────────
  function Skeleton() {
    return (
      <div style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.05)',borderRadius:12,padding:'10px 12px',marginBottom:8,display:'flex',alignItems:'center',gap:10}}>
        <div style={{width:42,height:42,borderRadius:10,flexShrink:0,background:'rgba(255,255,255,0.07)',animation:'skeletonPulse 1.4s ease-in-out infinite'}}/>
        <div style={{flex:1}}>
          <div style={{height:12,width:'55%',borderRadius:5,background:'rgba(255,255,255,0.07)',marginBottom:8,animation:'skeletonPulse 1.4s ease-in-out 0.1s infinite'}}/>
          <div style={{height:9,width:'75%',borderRadius:4,background:'rgba(255,255,255,0.05)',animation:'skeletonPulse 1.4s ease-in-out 0.2s infinite'}}/>
        </div>
        <div style={{width:50,height:36,borderRadius:6,background:'rgba(255,255,255,0.05)',animation:'skeletonPulse 1.4s ease-in-out 0.15s infinite'}}/>
      </div>
    );
  }

  // ─── LIST ITEMS ─────────────────────────────────────────────────────────────
  function ListItems() {
    if (!displayFiltered.length) {
      // Akıllı filtre kaldırma önerileri — her filtre tek tek kaldırılırsa kaç sonuç açılır?
      const normTR = s => (s||'').replace(/İ/g,'i').replace(/I/g,'i').replace(/Ğ/g,'g').replace(/Ş/g,'s').replace(/Ü/g,'u').replace(/Ö/g,'o').replace(/Ç/g,'c').replace(/ğ/g,'g').replace(/ş/g,'s').replace(/ü/g,'u').replace(/ö/g,'o').replace(/ç/g,'c').replace(/ı/g,'i').toLowerCase();
      const tryWithout = (skip) => {
        return places.filter(p => {
          if (skip!=='cat' && activeCategory!=='all' && p.type!==activeCategory) return false;
          if (skip!=='il' && selectedIl && normTR(p.city)!==normTR(selectedIl)) return false;
          if (skip!=='ilce' && selectedIlce && ilceBbox && !(p.lng>=ilceBbox[0]&&p.lat>=ilceBbox[1]&&p.lng<=ilceBbox[2]&&p.lat<=ilceBbox[3])) return false;
          if (skip!=='mah' && selectedMahalleler.length>0 && !selectedMahalleler.some(m=>hav(m.center[1],m.center[0],p.lat,p.lng)<1.5)) return false;
          if (skip!=='price' && (priceRange[0]>0||priceRange[1]<6000) && !(p.priceNum===0||(p.priceNum>=priceRange[0]&&p.priceNum<=priceRange[1]))) return false;
          if (skip!=='age' && selectedAgeGroup) {
            const [minA, maxA] = selectedAgeGroup === '12+' ? [12, 99] : selectedAgeGroup.split('-').map(Number);
            if (!(p.ageMax>=minA && p.ageMin<=maxA)) return false;
          }
          if (skip!=='radius' && radiusKm && upLat!=null && hav(upLat,upLng,p.lat,p.lng)>radiusKm) return false;
          return true;
        }).length;
      };
      // Bütçe artırma simülasyonu — fiyat aralığını %50 genişletirse kaç açılır?
      const expandBudget = () => {
        const newMax = Math.min(50000, Math.round(priceRange[1] * 1.5));
        return places.filter(p => {
          if (activeCategory!=='all' && p.type!==activeCategory) return false;
          if (selectedIl && normTR(p.city)!==normTR(selectedIl)) return false;
          if (selectedIlce && ilceBbox && !(p.lng>=ilceBbox[0]&&p.lat>=ilceBbox[1]&&p.lng<=ilceBbox[2]&&p.lat<=ilceBbox[3])) return false;
          if (selectedMahalleler.length>0 && !selectedMahalleler.some(m=>hav(m.center[1],m.center[0],p.lat,p.lng)<1.5)) return false;
          if (!(p.priceNum===0||(p.priceNum>=priceRange[0]&&p.priceNum<=newMax))) return false;
          if (selectedAgeGroup) {
            const [minA, maxA] = selectedAgeGroup === '12+' ? [12, 99] : selectedAgeGroup.split('-').map(Number);
            if (!(p.ageMax>=minA && p.ageMin<=maxA)) return false;
          }
          if (radiusKm && upLat!=null && hav(upLat,upLng,p.lat,p.lng)>radiusKm) return false;
          return true;
        }).length;
      };
      // Yarıçap genişletme — radius x2
      const expandRadius = () => {
        if (!radiusKm || upLat==null) return 0;
        const newR = radiusKm * 2;
        return places.filter(p => {
          if (activeCategory!=='all' && p.type!==activeCategory) return false;
          if (selectedIl && normTR(p.city)!==normTR(selectedIl)) return false;
          if (selectedIlce && ilceBbox && !(p.lng>=ilceBbox[0]&&p.lat>=ilceBbox[1]&&p.lng<=ilceBbox[2]&&p.lat<=ilceBbox[3])) return false;
          if (selectedMahalleler.length>0 && !selectedMahalleler.some(m=>hav(m.center[1],m.center[0],p.lat,p.lng)<1.5)) return false;
          if ((priceRange[0]>0||priceRange[1]<6000) && !(p.priceNum===0||(p.priceNum>=priceRange[0]&&p.priceNum<=priceRange[1]))) return false;
          if (selectedAgeGroup) {
            const [minA, maxA] = selectedAgeGroup === '12+' ? [12, 99] : selectedAgeGroup.split('-').map(Number);
            if (!(p.ageMax>=minA && p.ageMin<=maxA)) return false;
          }
          if (hav(upLat,upLng,p.lat,p.lng)>newR) return false;
          return true;
        }).length;
      };
      const baseCandidates = [
        activeCategory!=='all' && {key:'cat',label:'Kategori filtresini kaldır',action:()=>setActiveCategory('all')},
        selectedAgeGroup && {key:'age',label:selectedAgeGroup+' yaş filtresini kaldır',action:()=>setSelectedAgeGroup(null)},
        (priceRange[0]>0||priceRange[1]<6000) && {key:'price',label:'Fiyat aralığını kaldır',action:()=>setPriceRange([0,6000])},
        radiusKm && {key:'radius',label:radiusKm+' km yarıçap kısıtını kaldır',action:()=>setRadiusKm(null)},
        selectedMahalleler.length>0 && {key:'mah',label:'Mahalle seçimini kaldır',action:()=>setSelectedMahalleler([])},
        selectedIlce && {key:'ilce',label:selectedIlce+' ilçesini kaldır (komşu ilçelere bak)',action:()=>setSelectedIlce('')},
        selectedIl && {key:'il',label:selectedIl+' il kısıtını kaldır',action:()=>{ setSelectedIl(''); setSelectedIlce(''); setSelectedMahalleler([]); }},
      ].filter(Boolean).map(c => ({...c, gain: tryWithout(c.key)}));
      const expansionCandidates = [
        priceRange[1]<50000 && {key:'expand-budget',label:'Bütçeni ₺'+Math.round(priceRange[1]*1.5).toLocaleString('tr')+'\'e çıkar',action:()=>setPriceRange([priceRange[0], Math.min(50000,Math.round(priceRange[1]*1.5))]),gain:expandBudget()-(displayFiltered?.length||0)},
        radiusKm && upLat!=null && {key:'expand-radius',label:'Arama yarıçapını '+(radiusKm*2)+' km\'ye çıkar',action:()=>setRadiusKm(radiusKm*2),gain:expandRadius()-(displayFiltered?.length||0)},
      ].filter(Boolean);
      const candidates = [...baseCandidates, ...expansionCandidates].filter(c => c.gain>0).sort((a,b)=>b.gain-a.gain).slice(0,4);
      return (
        <div style={{textAlign:'center',padding:'40px 16px'}}>
          <div style={{width:80,height:80,margin:'0 auto 12px',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <Maskot type="mod" name="sad" size={80}/>
          </div>
          <div style={{color:C('txt'),fontSize:14,fontWeight:700,marginBottom:6}}>Sonuç bulunamadı</div>
          <div style={{color:C('txt2'),fontSize:12,lineHeight:1.6,marginBottom:14}}>{candidates.length>0 ? 'Aşağıdaki filtreleri kaldırarak daha fazla okul gör:' : 'Filtrelerini gevşetmeyi dene'}</div>
          {candidates.length>0 && (
            <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:12,padding:'0 8px'}}>
              {candidates.map(c=>(
                <button key={c.key} onClick={c.action}
                  style={{padding:'10px 14px',borderRadius:10,background:'rgba(168,218,255,0.08)',border:'1px solid rgba(168,218,255,0.25)',color:C('txt'),fontSize:12,cursor:'pointer',fontFamily:'DM Sans,sans-serif',display:'flex',justifyContent:'space-between',alignItems:'center',textAlign:'left',gap:8}}>
                  <span>{c.label}</span>
                  <span style={{color:'#A8DAFF',fontWeight:700,fontSize:11,whiteSpace:'nowrap'}}>+{c.gain} okul</span>
                </button>
              ))}
            </div>
          )}
          <button onClick={()=>{ setActiveCategory('all'); setPriceRange([0,6000]); setSelectedAgeGroup(null); setSearch(''); setSelectedMahalleler([]); setSelectedIlce(''); setRadiusKm(null); }}
            style={{padding:'8px 18px',borderRadius:10,background:'rgba(168,218,255,0.12)',border:'1px solid rgba(168,218,255,0.3)',color:'#A8DAFF',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
            Tüm Filtreleri Temizle
          </button>
        </div>
      );
    }
    const openDetail = (p) => {
      setDetailId(p.id);
      setRecentIds(prev => { const next=[p.id,...prev.filter(x=>x!==p.id)].slice(0,10); try{localStorage.setItem('okulmap_recent',JSON.stringify(next));}catch(e){} return next; });
    };
    if (listView === 'grid') {
      const sliced = displayFiltered.slice(0, listLimit);
      // GENEL KURAL: >50 item → stagger YOK (sadece fadeIn). ≤50 → idx*0.01 max.
      const noStagger = sliced.length > 50;
      return (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {sliced.map((p,idx)=>{
            const color = p.sponsored?'#FFD700':TC[p.type]; const cat=CATS.find(c=>c.id===p.type)||{};
            const fillPct=Math.round((p.capacity>0?p.filled/p.capacity:0)*100);
            const fillColor=fillPct>80?'#FF6B6B':fillPct>60?'#FFE66D':'#4ECDC4';
            const anim = noStagger ? 'fadeIn 0.3s ease both' : 'slideUp 0.3s ease '+(Math.min(idx,20)*0.01)+'s both';
            return (
              <div key={p.id} className="card-hover"
                style={{background:C('surf2'),border:p.sponsored?'1px solid rgba(255,215,0,0.35)':'1px solid '+C('bord'),borderRadius:12,padding:'10px',animation:anim,position:'relative',overflow:'hidden'}}>
                {p.sponsored&&<div style={{position:'absolute',top:0,left:0,right:0,height:2,background:'linear-gradient(90deg,transparent,#FFD700,transparent)',animation:'sponsorSheen 2s linear infinite'}}/>}
                {(()=>{ const sc=getScore(p); return sc!=null ? <div style={{position:'absolute',top:6,right:6,padding:'2px 6px',borderRadius:10,background:scoreColor(sc)+'20',border:'1px solid '+scoreColor(sc)+'55',color:scoreColor(sc),fontSize:9,fontWeight:800,fontFamily:'Syne,sans-serif'}}>%{sc}</div> : null; })()}
                <div style={{width:36,height:36,borderRadius:9,background:color+'18',border:'1px solid '+color+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,marginBottom:7}}>{cat.icon||'🏫'}</div>
                <div style={{color:C('txt'),fontSize:11,fontWeight:700,marginBottom:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                <div style={{color:'#FFE66D',fontSize:10,marginBottom:3}}>★ {p.rating} <span style={{color:C('txt3')}}>{p.age} yaş</span></div>
                <div style={{color:p.priceNum>0?color:C('txt3'),fontSize:11,fontWeight:p.priceNum>0?700:500,marginBottom:4}}>{p.price}</div>
                {p.open&&p.capacity>0&&<div style={{height:3,borderRadius:2,background:C('bord2'),overflow:'hidden',marginBottom:6}}><div style={{height:'100%',width:fillPct+'%',background:fillColor}}/></div>}
                <button onClick={()=>openDetail(p)} style={{width:'100%',padding:'4px 0',borderRadius:6,background:color+'20',border:'1px solid '+color+'40',color:color,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginTop:4}}>Detay</button>
              </div>
            );
          })}
          {displayFiltered.length > listLimit && (
            <div style={{gridColumn:'1 / -1',display:'flex',justifyContent:'center',padding:'10px 0'}}>
              <button onClick={()=>setListLimit(l => l + 50)} style={{padding:'8px 20px',borderRadius:10,background:C('inp'),border:'1px solid '+C('bord'),color:C('txt2'),fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                ↓ Daha fazla göster ({displayFiltered.length - listLimit})
              </button>
            </div>
          )}
        </div>
      );
    }
    const slicedList = displayFiltered.slice(0, listLimit);
    const moreBtn = displayFiltered.length > listLimit ? (
      <div key="__more__" style={{display:'flex',justifyContent:'center',padding:'10px 0'}}>
        <button onClick={()=>setListLimit(l => l + 50)} style={{padding:'8px 20px',borderRadius:10,background:C('inp'),border:'1px solid '+C('bord'),color:C('txt2'),fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
          ↓ Daha fazla göster ({displayFiltered.length - listLimit})
        </button>
      </div>
    ) : null;
    const cards = slicedList.map(function(p, idx){
      var color = p.sponsored ? '#FFD700' : TC[p.type];
      var cat = CATS.find(function(c){return c.id === p.type;}) || {};
      var isSel = String(selectedId) === String(p.id);
      var fillPct = Math.round((p.capacity > 0 ? p.filled/p.capacity : 0)*100);
      var fillColor = fillPct>80?'#FF6B6B':fillPct>60?'#FFE66D':'#4ECDC4';
      var ageMatch = childAge >= (p.ageMin||0) && childAge <= (p.ageMax||18);
      var cmp = campaignsBySchool[p.id];
      var cmpTypeMap = {erken_kayit:{c:'#4ADE80',e:'🎯'},acik_kapi:{c:'#A78BFA',e:'📅'},indirim:{c:'#FB923C',e:'💰'},etkinlik:{c:'#60A5FA',e:'🎉'},duyuru:{c:'#F472B6',e:'📢'},promo:{c:'#FFE66D',e:'🎁'},event:{c:'#4ECDC4',e:'🎉'},new:{c:'#FF6B8A',e:'📢'}};
      var cmpStyle = cmp ? (cmpTypeMap[cmp.type] || {c:'#FF9500',e:'🎯'}) : null;
      return (
        <div key={p.id} className="card-hover"
          style={{
            background: isSel ? 'rgba(255,255,255,0.05)' : 'transparent',
            border: p.sponsored ? '1px solid rgba(255,215,0,0.4)' : cmp ? '1px solid '+(cmpStyle.c+'40') : '1px solid '+(isSel?color+'50':C('bord')),
            borderRadius:12, padding:'11px 13px', marginBottom:8, cursor:'pointer',
            transition:'all 0.2s', display:'flex', alignItems:'center', gap:10,
            animation: (slicedList.length > 50 ? 'fadeIn 0.3s ease both' : 'slideUp 0.3s ease '+(Math.min(idx,20)*0.01)+'s both'), WebkitTapHighlightColor:'transparent',
            position:'relative', overflow:'hidden',
            opacity: mode === 'zone' && !ageMatch ? 0.35 : 1,
          }}
          onClick={function(){
            setSelectedId(function(prev){return String(p.id) === String(prev) ? null : String(p.id);});
            if (mapRef.current) {
              mapRef.current.flyTo({center:[p.lng,p.lat],zoom:16,duration:800});
              setTimeout(function(){ var m=markersRef.current[p.id]; if(m&&!m.getPopup().isOpen()) m.togglePopup(); }, 900);
            }
          }}>
          {p.sponsored && <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:'linear-gradient(90deg,transparent,#FFD700,transparent)',backgroundSize:'200% auto',animation:'sponsorSheen 2s linear infinite'}}/>}
          {p.sponsored && <div style={{position:'absolute',top:6,right:6,background:'rgba(255,215,0,0.18)',border:'1px solid rgba(255,215,0,0.4)',borderRadius:4,padding:'1px 5px',fontSize:8,color:'#FFD700',fontWeight:700,letterSpacing:'0.05em'}}>★ ÖNE ÇIKAN</div>}
          <div style={{width:42,height:42,borderRadius:10,flexShrink:0,background:color+'18',border:'1px solid '+color+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>{cat.icon||''}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:2}}>
              <span style={{color:C('txt'),fontSize:12,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</span>
              {!p.open && <span style={{background:'rgba(255,255,255,0.06)',borderRadius:4,padding:'1px 4px',fontSize:8,color:C('txt3'),flexShrink:0}}>KAPALI</span>}
              {p.okulbot && <span style={{background:'rgba(168,218,255,0.1)',borderRadius:4,padding:'1px 4px',fontSize:8,color:'#A8DAFF',flexShrink:0}}>✦</span>}
              {(()=>{ const sc=getScore(p); return sc!=null ? <span style={{background:scoreColor(sc)+'22',border:'1px solid '+scoreColor(sc)+'55',borderRadius:4,padding:'1px 5px',fontSize:9,color:scoreColor(sc),fontWeight:800,flexShrink:0,fontFamily:'Syne,sans-serif'}}>%{sc}</span> : null; })()}
              {mode === 'zone' && (ageMatch
                ? <span style={{background:'rgba(78,205,196,0.15)',border:'1px solid rgba(78,205,196,0.4)',borderRadius:4,padding:'1px 5px',fontSize:8,color:'#4ECDC4',fontWeight:700,flexShrink:0}}>✓ Uygun</span>
                : <span style={{background:'rgba(255,107,107,0.15)',border:'1px solid rgba(255,107,107,0.4)',borderRadius:4,padding:'1px 5px',fontSize:8,color:'#FF6B6B',fontWeight:700,flexShrink:0}}>✗</span>
              )}
            </div>
            {cmp && cmpStyle && (
              <div style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 7px',borderRadius:6,marginBottom:4,fontSize:10,fontWeight:700,background:cmpStyle.c+'15',border:'1px solid '+cmpStyle.c+'30',color:cmpStyle.c,maxWidth:'100%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {cmpStyle.e} {cmp.title}
              </div>
            )}
            <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:5}}>
              <span style={{color:C('txt2'),fontSize:10}}>{p.distLabel!=='—'?'📍 '+p.distLabel:'👶 '+p.age}</span>
              <span style={{color:'#FFE66D',fontSize:10}}>★ {p.rating}</span>
              <span style={{color:C('txt2'),fontSize:10}}>{p.age} yaş</span>
            </div>
            {p.open && p.capacity > 0 && (
              <div style={{display:'flex',alignItems:'center',gap:5}}>
                <div style={{flex:1,height:3,borderRadius:2,background:C('bord2'),overflow:'hidden'}}>
                  <div style={{height:'100%',width:fillPct+'%',background:fillColor,borderRadius:2,transition:'width 0.6s'}}/>
                </div>
                <span style={{color:fillColor,fontSize:9,fontWeight:700,flexShrink:0}}>{fillPct}%</span>
              </div>
            )}
            {(()=>{
              const vbadges=(allSchoolBadges[p.id]||[]).filter(b=>b.verified).slice(0,3);
              if(!vbadges.length) return null;
              return (
                <div style={{display:'flex',flexWrap:'wrap',gap:3,marginTop:4}}>
                  {vbadges.map(b=>{
                    const def=BADGES.find(d=>d.key===b.key);
                    if(!def) return null;
                    return <span key={b.key} style={{display:'inline-flex',alignItems:'center',gap:2,padding:'2px 6px',borderRadius:20,fontSize:9,fontWeight:600,background:'rgba(78,205,196,0.12)',border:'1px solid rgba(78,205,196,0.25)',color:'#4ECDC4'}}>{def.emoji} {def.label}</span>;
                  })}
                </div>
              );
            })()}
          </div>
          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,flexShrink:0}}>
            <span style={{color:p.priceNum>0?color:C('txt3'),fontSize:11,fontWeight:p.priceNum>0?700:500}}>{p.price}</span>
            <button onClick={function(e){ e.stopPropagation(); openDetail(p); }}
              style={{padding:'3px 8px',borderRadius:5,background:color+'20',border:'1px solid '+color+'40',color:color,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
              Detay
            </button>
          </div>
        </div>
      );
    });
    return moreBtn ? [...cards, moreBtn] : cards;
  }

  // ─── RIGHT PANEL ────────────────────────────────────────────────────────────
  function RightPanel() {
    const featuredPlaces = places.filter(p=>p.sponsored).slice(0,3);
    return (
      <div style={{width:280,flexShrink:0,borderLeft:'1px solid '+C('bord'),background:C('bg'),overflowY:'auto',display:'flex',flexDirection:'column',gap:0}}>
        {/* Kampanyalar */}
        <div style={{padding:'14px 14px 10px',borderBottom:'1px solid '+C('bord')}}>
          <div style={{fontSize:11,fontWeight:700,color:C('txt3'),letterSpacing:'0.08em',marginBottom:10}}>🎯 KAMPANYALAR</div>
          {campaigns.length===0 ? (
            <div style={{color:C('txt3'),fontSize:11,textAlign:'center',padding:'12px 0'}}>Aktif kampanya yok</div>
          ) : campaigns.map(c=>{
            const typeMap = {erken_kayit:{color:'#4ADE80',icon:'🎯'},acik_kapi:{color:'#A78BFA',icon:'📅'},indirim:{color:'#FB923C',icon:'💰'},etkinlik:{color:'#60A5FA',icon:'🎉'},duyuru:{color:'#F472B6',icon:'📢'},promo:{color:'#FFE66D',icon:'🎁'},event:{color:'#4ECDC4',icon:'🎉'},new:{color:'#FF6B8A',icon:'📢'}};
            const t = typeMap[c.type] || {color:'#A8DAFF',icon:'📌'};
            const days = c.expires_at ? Math.ceil((new Date(c.expires_at).getTime()-Date.now())/86400000) : null;
            const school = places.find(p=>p.id===c.school_id);
            const schoolCat = CATS.find(cat=>cat.id===school?.type) || {icon:'📚',color:'#A8DAFF'};
            return (
              <div key={c.id} style={{background:t.color+'08',border:'1px solid '+t.color+'50',borderRadius:10,marginBottom:8,cursor:'pointer','--glow-color':t.color+'55',animation:'campaignGlow 2.4s ease-in-out infinite'}}
                className="card-hover"
                onClick={()=>{
                  try { supabase.rpc('increment_campaign_click', { campaign_id: c.id }).catch(()=>{}); } catch {}
                  if(school) setDetailId(school.id);
                }}>
                <div style={{padding:'9px 11px',display:'flex',alignItems:'flex-start',gap:9}}>
                  {/* school type icon */}
                  <div style={{width:32,height:32,borderRadius:8,background:schoolCat.color+'18',border:'1px solid '+schoolCat.color+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>
                    {schoolCat.icon}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:2}}>
                      <span style={{fontSize:11}}>{t.icon}</span>
                      <span style={{color:t.color,fontSize:11,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{c.title}</span>
                      {days!==null&&days>=0&&<span style={{fontSize:9,fontWeight:700,color:days<=7?'#FB923C':C('txt3'),flexShrink:0,background:days<=7?'rgba(251,146,60,0.12)':'rgba(255,255,255,0.06)',padding:'1px 4px',borderRadius:4}}>{days}g</span>}
                    </div>
                    {school&&<div style={{color:C('txt3'),fontSize:10,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>🏫 {school.name}</div>}
                    <div style={{color:C('txt2'),fontSize:10,lineHeight:1.4,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{c.body}</div>
                    {c.expires_at&&<div style={{color:C('txt3'),fontSize:9,marginTop:3}}>⏳ {new Date(c.expires_at).toLocaleDateString('tr-TR')}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Öne Çıkan Okullar */}
        <div style={{padding:'14px 14px 10px'}}>
          <div style={{fontSize:11,fontWeight:700,color:C('txt3'),letterSpacing:'0.08em',marginBottom:10}}>🌟 ÖNE ÇIKAN OKULLAR</div>
          {(featuredPlaces.length>0?featuredPlaces:places.slice(0,3)).map(p=>{
            const color = TC[p.type]||'#A8DAFF';
            const cat = CATS.find(c=>c.id===p.type)||{};
            return (
              <div key={p.id} onClick={()=>setDetailId(p.id)}
                style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid '+C('bord'),cursor:'pointer'}}
                className="card-hover">
                <div style={{width:36,height:36,borderRadius:9,background:color+'18',border:'1px solid '+color+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>{cat.icon||'🏫'}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:C('txt'),fontSize:11,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                  <div style={{color:C('txt2'),fontSize:10,marginTop:1}}>★ {p.rating} · {p.price}</div>
                </div>
                {p.sponsored && <span style={{background:'rgba(255,215,0,0.15)',border:'1px solid rgba(255,215,0,0.3)',borderRadius:4,padding:'1px 5px',fontSize:8,color:'#FFD700',fontWeight:700,flexShrink:0}}>ÖZEL</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  const selPlace = selectedId ? places.find(x=>String(x.id)===String(selectedId)) : null;
  const selColor = selPlace ? TC[selPlace.type] : null;
  const selCat = selPlace ? (CATS.find(c=>c.id===selPlace.type)||{}) : {};

  return (
    <div style={{height:'100dvh',background:C('bg'),display:'flex',flexDirection:'column',overflow:'hidden',transition:'background 0.3s'}}>
      {staffInfo && !staffBannerDismissed && (
        <div style={{flexShrink:0,padding:'10px 16px',background:'linear-gradient(135deg,#1E1048,#3b1d6e)',color:'#fff',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:10,flexWrap:'wrap',fontFamily:'DM Sans,sans-serif'}}>
          <span>👤 Hesabınız okul yönetim panelinde.</span>
          <a href={staffInfo.url || 'https://app.okulmap.com'} style={{color:'#fff',textDecoration:'underline',fontWeight:700}}>Panele git →</a>
          <button onClick={()=>{
            localStorage.setItem('okulmap_skip_admin_redirect','1');
            localStorage.setItem('okulmap_staff_banner_dismissed','1');
            setStaffBannerDismissed(true);
          }}
            style={{background:'rgba(255,255,255,0.18)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',padding:'4px 10px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>Anasayfada kal</button>
        </div>
      )}
      <LocationOnboardingModal
        theme={theme} isMobile={isMobile} MAPBOX_TOKEN={MAPBOX_TOKEN}
        showLocationModal={showLocationModal} setShowLocationModal={setShowLocationModal}
        locModalStep={locModalStep} setLocModalStep={setLocModalStep}
        locModalGpsError={locModalGpsError} setLocModalGpsError={setLocModalGpsError}
        setUserPos={setUserPos} setSortBy={setSortBy} setLocLabel={setLocLabel} setMobileTab={setMobileTab}
        mapRef={mapRef} showToast={showToast} setShowLocPermModal={setShowLocPermModal}
        locModalIl={locModalIl} locModalIlce={locModalIlce}
        setSelectedIl={setSelectedIl} setSelectedIlce={setSelectedIlce} ilceCenterRef={ilceCenterRef}
      />
      <LocationPermissionModal
        showLocPermModal={showLocPermModal} setShowLocPermModal={setShowLocPermModal}
        openLocationModal={openLocationModal} setLocModalStep={setLocModalStep}
      />
      <AuthModal theme={theme} showAuth={showAuth} setShowAuth={setShowAuth} authMode={authMode} setAuthMode={setAuthMode} authShowEmail={authShowEmail} setAuthShowEmail={setAuthShowEmail} />
      {detailId && (()=>{
        const _dp = places.find(x=>String(x.id)===String(detailId));
        if (!_dp) return null;
        return (
          <DetailModal
            place={_dp}
            theme={theme}
            user={user}
            favs={favs}
            compareList={compareList}
            schoolBadges={schoolBadges}
            toggleFav={toggleFav}
            handleCompare={handleCompare}
            onClose={()=>setDetailId(null)}
            onOpenInstitution={()=>{ setInstProfileId(_dp.id); setDetailId(null); }}
            onBilgiAl={()=>{ setShowBilgiAl(_dp); trackAnalytics(_dp.id,'bilgi_al'); }}
            onClaim={()=>setShowClaimModal(_dp)}
            onShare={async ()=>{
              let slug = _dp.slug;
              if (!slug) {
                slug = await generateUniqueSlug(_dp.name);
                await supabase.from('schools').update({ slug }).eq('id', _dp.id);
                setPlaces(prev => prev.map(x => String(x.id) === String(_dp.id) ? { ...x, slug } : x));
                placesRef.current = placesRef.current.map(x => String(x.id) === String(_dp.id) ? { ...x, slug } : x);
              }
              const url = `https://okulmap.com?okul=${slug}`;
              navigator.clipboard.writeText(url).catch(()=>{});
              if (navigator.share) {
                navigator.share({ title: _dp.name, text: `${_dp.name} — OkulMap'te incele!`, url }).catch(()=>{});
              } else {
                showToast('🔗 Link kopyalandı!');
              }
              trackAnalytics(_dp.id, 'share');
            }}
            onAlert={()=>setShowAlertModal(_dp)}
            onShowAuth={(v)=>{ setShowAuth(v); setAuthShowEmail(true); }}
            showToast={showToast}
            trackAnalytics={trackAnalytics}
            onWalkRoute={()=>handleWalkRoute(_dp)}
            onClearWalkRoute={handleClearWalkRoute}
            walkRoute={walkRoute}
            userPrefs={userPrefs}
            onOpenPrefs={()=>setShowPrefsModal(true)}
          />
        );
      })()}
      {showPrefsModal && <PreferencesModal theme={theme} open={showPrefsModal} onClose={()=>setShowPrefsModal(false)} onSaved={(p)=>setUserPrefs(p)} />}
      {showBilgiAl && <BilgiAlModal place={showBilgiAl} theme={theme} userProfile={userProfile} onClose={()=>setShowBilgiAl(null)} />}
      {showAlertModal && <AlertModal place={showAlertModal} theme={theme} user={user} onClose={()=>setShowAlertModal(null)} />}
      {showClaimModal && <ClaimModal place={showClaimModal} theme={theme} user={user} onClose={()=>setShowClaimModal(null)} />}
      {showCompare && <CompareModal theme={theme} isMobile={isMobile} compareList={compareList} setCompareList={setCompareList} setShowCompare={setShowCompare} places={places} onShareToast={showToast} />}
      {showAddSchool && <AddSchoolModal theme={theme} MAPBOX_TOKEN={MAPBOX_TOKEN} onClose={()=>setShowAddSchool(false)} />}
      {showSupport && <SupportFormModal theme={theme} onClose={()=>setShowSupport(false)} source="anasayfa" />}
      <InstitutionProfileModal theme={theme} instProfileId={instProfileId} setInstProfileId={setInstProfileId} setDetailId={setDetailId} places={places} />
      {/* PWA Install Banner */}
      {showPwaPrompt && (
        <div style={{position:'fixed',bottom:isMobile?72:24,left:'50%',transform:'translateX(-50%)',background:'rgba(20,25,36,0.97)',backdropFilter:'blur(12px)',border:'1px solid rgba(168,218,255,0.25)',borderRadius:14,padding:'12px 20px',zIndex:9998,display:'flex',alignItems:'center',gap:12,maxWidth:360,boxShadow:'0 8px 32px rgba(0,0,0,0.5)',animation:'toastSlide 0.25s ease'}}>
          <img src="/logo.png?v=3" alt="OkulMap" style={{height:36,objectFit:'contain',flexShrink:0}}/>
          <div style={{flex:1}}>
            <div style={{color:'#fff',fontSize:13,fontWeight:700}}>Ana Ekrana Ekle</div>
            <div style={{color:'rgba(255,255,255,0.5)',fontSize:11,marginTop:1}}>OkulMap'i hızlıca aç</div>
          </div>
          <button onClick={async()=>{ if(pwaPromptRef.current){pwaPromptRef.current.prompt();const r=await pwaPromptRef.current.userChoice;if(r.outcome==='accepted')setShowPwaPrompt(false);} }}
            style={{padding:'7px 14px',borderRadius:9,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',border:'none',color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',flexShrink:0}}>Ekle</button>
          <button onClick={()=>{ setShowPwaPrompt(false); localStorage.setItem('okulmap_pwa_dismissed','1'); }}
            style={{width:26,height:26,borderRadius:7,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.5)',cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>✕</button>
        </div>
      )}

      {/* Toast */}
      {toast && (() => {
        const t = String(toast);
        const isError = /⚠️|❌|hata|alınamadı|bulunamadı|izin|başarısız/i.test(t);
        const isSuccess = /✅|🎉|başarıyla|kaydedildi|tamamlandı|gönderildi|alındı/i.test(t);
        const modName = isError ? 'sad' : isSuccess ? 'excited' : 'merhaba';
        const accent = isError ? 'rgba(255,107,107,0.3)' : isSuccess ? 'rgba(78,205,196,0.35)' : 'rgba(168,218,255,0.25)';
        return (
          <div style={{position:'fixed',bottom:isMobile?76:24,left:'50%',transform:'translateX(-50%)',background:'rgba(20,25,36,0.97)',backdropFilter:'blur(12px)',border:'1px solid '+accent,borderRadius:12,padding:'10px 16px 10px 12px',color:'#fff',fontSize:13,fontWeight:600,fontFamily:'DM Sans,sans-serif',zIndex:9999,animation:'toastSlide 0.25s ease',display:'flex',alignItems:'center',gap:10,maxWidth:'calc(100vw - 32px)',boxShadow:'0 8px 32px rgba(0,0,0,0.5)'}}>
            <Maskot type="mod" name={modName} size={28} style={{flexShrink:0}}/>
            <span>{t}</span>
          </div>
        );
      })()}

      {/* ── Desktop header (hidden on mobile) ── */}
      <div style={{display: isMobile ? 'none' : 'block', flexShrink:0}}>
        {Nav()}
        {ModeBar()}
      </div>
      {/* ── Mobile header (hidden on desktop) ── */}
      <div style={{display: isMobile ? 'flex' : 'none', flexDirection:'column', flexShrink:0, position:'sticky', top:0, zIndex:100}}>
        <MobileTopBar
          theme={theme} MAPBOX_TOKEN={MAPBOX_TOKEN}
          mode={mode} setMode={setMode} showEvents={showEvents} setShowEvents={setShowEvents} setTheme={setTheme}
          user={user} setShowAuth={setShowAuth} setMobileTab={setMobileTab}
          search={search} setSearch={setSearch} places={places} mapRef={mapRef} setDetailId={setDetailId}
          openLocationModal={openLocationModal}
          selectedIl={selectedIl} setSelectedIl={setSelectedIl} selectedIlce={selectedIlce} setSelectedIlce={setSelectedIlce}
          selectedMahalleler={selectedMahalleler} setSelectedMahalleler={setSelectedMahalleler} mahalleResults={mahalleResults}
          onAddSchool={()=>setShowAddSchool(true)}
          onSupport={()=>setShowSupport(true)}
        />
        <MobileCategoryBar
          theme={theme} activeCategory={activeCategory} setActiveCategory={setActiveCategory}
          showAllCats={showAllCats} setShowAllCats={setShowAllCats} catBarRef={catBarRef}
        />
      </div>

      {/* ── Main layout: left panel + map column ── */}
      <div id="main-layout" style={{flex:1,display:'flex',flexDirection: isMobile ? 'column' : 'row',overflow:'hidden',paddingBottom: isMobile ? 'calc(62px + env(safe-area-inset-bottom,0px))' : 0}}>

        {/* Left panel — desktop only */}
        <div style={{display: isMobile ? 'none' : 'flex', flexShrink:0}}>
          {LeftPanel()}
        </div>

        {/* Right column: map + mobile content panels — ALWAYS SAME TREE POSITION */}
        <div style={{flex:1,display:'flex',flexDirection:'row',overflow:'hidden',position:'relative'}}>
        <div style={{flex:1,display: isMobile && mobileTab==='compare' ? 'none' : 'flex',flexDirection:'column',overflow: isMobile && mobileTab==='map' ? 'auto' : 'hidden',position:'relative',WebkitOverflowScrolling:'touch'}}>

          {/* MAP PANEL — always in same tree position, never unmounts */}
          <div id="map-panel" style={isMobile ? {
            display: mobileTab==='map' ? 'block' : 'none',
            height: 230,
            flexShrink:0,
            position:'relative',
            overflow:'hidden',
            margin: '12px 16px 0',
            borderRadius: 20,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          } : {
            flex:1,
            position:'relative',
            minHeight:200,
          }}>
            <div ref={mapContainerRef} id="map-box" style={{width:'100%',height:'100%'}}/>
            <div id="loc-label" style={{position:'absolute',top:12,left:12,background:'rgba(10,13,25,0.88)',backdropFilter:'blur(8px)',borderRadius:8,padding:'4px 10px',border:'1px solid rgba(255,255,255,0.1)',fontSize:11,color:'rgba(255,255,255,0.6)',zIndex:10,pointerEvents:'none'}}>
              📍 {locLabel}
            </div>
            {/* Floating özet pill — sol üst */}
            <div style={{position:'absolute',top:54,left:12,background:'rgba(10,13,25,0.88)',backdropFilter:'blur(8px)',borderRadius:20,padding:'4px 12px',border:'1px solid rgba(255,255,255,0.1)',fontSize:11,color:'rgba(255,255,255,0.7)',zIndex:10,pointerEvents:'none',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap',animation:'fadeIn 0.3s ease'}}>
              <span style={{color:'#A8DAFF',fontWeight:700}}>{displayFiltered.length}</span>
              <span>kurum</span>
              {(selectedIlce||selectedIl) && <><span style={{opacity:0.3}}>·</span><span style={{color:'rgba(255,255,255,0.5)'}}>{selectedIlce||selectedIl}</span></>}
              {activeCategory!=='all' && <><span style={{opacity:0.3}}>·</span><span style={{color:(CATS.find(c=>c.id===activeCategory)||{}).color||'#A8DAFF'}}>{(CATS.find(c=>c.id===activeCategory)||{}).label}</span></>}
            </div>
            {/* Mobile harita overlay kontrolleri */}
            {isMobile && mobileTab==='map' && (
              <div style={{position:'absolute',top:12,right:12,display:'flex',flexDirection:'column',gap:7,zIndex:10}}>
                <button onClick={()=>{ console.log('HEAT BUTTON CLICKED, current mode:', mode); setMode(mode==='heat'?'normal':'heat'); }}
                  style={{width:36,height:36,borderRadius:10,background:mode==='heat'?'rgba(251,146,60,0.25)':C('nav'),backdropFilter:'blur(8px)',border:'1px solid '+(mode==='heat'?'rgba(251,146,60,0.5)':C('bord')),display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
                  <HugeiconsIcon icon={FireFreeIcons} size={16} color={mode==='heat'?'#FB923C':C('txt3')} />
                </button>
                <button onClick={handleGPS}
                  style={{width:36,height:36,borderRadius:10,background:C('nav'),backdropFilter:'blur(8px)',border:'1px solid rgba(168,218,255,0.35)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
                  <HugeiconsIcon icon={GpsFreeIcons} size={16} color="#A8DAFF" />
                </button>
                <button onClick={()=>setShowMobileFilter(true)}
                  style={{width:36,height:36,borderRadius:10,background:C('nav'),backdropFilter:'blur(8px)',border:'1px solid '+C('bord'),display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',position:'relative'}}>
                  <HugeiconsIcon icon={FilterFreeIcons} size={16} color="rgba(255,255,255,0.7)" />
                  {(priceRange[0]>0||priceRange[1]<6000)&&<div style={{position:'absolute',top:-3,right:-3,width:8,height:8,borderRadius:'50%',background:'#A8DAFF'}}/>}
                </button>
              </div>
            )}
            {mode === 'zone' && (()=>{
              const zonePct = (childAge / 16) * 100;
              const zoneMatch = filtered.filter(p => childAge >= (p.ageMin||0) && childAge <= (p.ageMax||18)).length;
              return (
                <div style={{
                  position:'absolute', bottom:52, left:'50%', transform:'translateX(-50%)',
                  background:C('nav'), backdropFilter:'blur(14px)',
                  borderRadius:18, padding:'12px 18px 10px',
                  border:'1px solid rgba(168,218,255,0.2)',
                  boxShadow:'0 8px 32px rgba(0,0,0,0.5)',
                  width:340, maxWidth:'calc(100vw - 32px)',
                  zIndex:15, userSelect:'none',
                }}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                    <div>
                      <div style={{color:C('txt'),fontSize:12,fontWeight:700,letterSpacing:'-0.2px'}}>🗺 Yaş Uyum Haritası</div>
                      <div style={{color:C('txt2'),fontSize:10,marginTop:2}}>
                        <span style={{color:'#A8DAFF',fontWeight:700}}>{zoneMatch}</span> kurum uygun
                      </div>
                    </div>
                    <div style={{background:'rgba(168,218,255,0.15)',border:'1px solid rgba(168,218,255,0.35)',borderRadius:12,padding:'6px 14px',textAlign:'center',minWidth:54}}>
                      <div style={{color:'#A8DAFF',fontSize:22,fontWeight:800,lineHeight:1,fontFamily:'Syne,sans-serif'}}>{childAge}</div>
                      <div style={{color:'rgba(168,218,255,0.6)',fontSize:9,marginTop:1}}>yaş</div>
                    </div>
                  </div>
                  <div ref={zoneSliderRef}
                    style={{position:'relative',height:24,cursor:'pointer',marginBottom:6,touchAction:'none'}}
                    onMouseDown={onZoneMouseDown}
                    onTouchStart={onZoneTouchStart}>
                    <div style={{position:'absolute',top:'50%',left:0,right:0,height:4,background:C('bord2'),borderRadius:2,transform:'translateY(-50%)',pointerEvents:'none'}}>
                      <div style={{position:'absolute',left:0,width:zonePct+'%',height:'100%',background:'linear-gradient(90deg,#7BB8F0,#A8DAFF)',borderRadius:2}}/>
                    </div>
                    <div style={{position:'absolute',top:'50%',left:zonePct+'%',transform:'translate(-50%,-50%)',width:20,height:20,background:'#A8DAFF',borderRadius:'50%',border:'2.5px solid rgba(255,255,255,0.9)',boxShadow:'0 2px 10px rgba(168,218,255,0.6)',cursor:'grab',pointerEvents:'none'}}/>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',paddingBottom:2}}>
                    {[0,2,4,6,8,10,12,14,16].map(a=>(
                      <span key={a} style={{fontSize:9,color:childAge===a?'#A8DAFF':C('txt3'),fontWeight:childAge===a?700:400}}>{a}</span>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div id="map-loading" style={{position:'absolute',inset:0,background:C('surf'),overflow:'hidden',zIndex:20}}>
              {/* Skeleton pin placeholders — varied positions, subtle pulse */}
              {[
                {top:'18%',left:'22%',d:0},{top:'27%',left:'58%',d:0.15},{top:'38%',left:'14%',d:0.3},
                {top:'42%',left:'72%',d:0.45},{top:'55%',left:'42%',d:0.6},{top:'63%',left:'66%',d:0.75},
                {top:'71%',left:'28%',d:0.9},{top:'78%',left:'82%',d:1.05},
              ].map((p,i)=>(
                <div key={i} style={{position:'absolute',top:p.top,left:p.left,width:32,height:32,borderRadius:'50% 50% 50% 0',transform:'rotate(-45deg)',background:'rgba(168,218,255,0.18)',border:'2px solid rgba(168,218,255,0.25)',animation:'mapSkeletonFade 1.6s ease-in-out '+p.d+'s infinite'}}/>
              ))}
              <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',pointerEvents:'none',gap:14}}>
                <Maskot type="mod" name="thinking" size={72} style={{filter:'drop-shadow(0 6px 18px rgba(168,218,255,0.35))'}}/>
                <div style={{padding:'8px 16px',borderRadius:20,background:'rgba(10,13,22,0.7)',backdropFilter:'blur(10px)',color:'rgba(255,255,255,0.65)',fontSize:12,fontFamily:'DM Sans,sans-serif',display:'flex',alignItems:'center',gap:8}}>
                  <span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#A8DAFF',animation:'mapSkeletonFade 1s ease-in-out infinite'}}/>
                  Harita yükleniyor
                </div>
              </div>
            </div>
            {/* Choropleth Lejant — sağ-alt, kategori barının hemen üstü */}
            <div className="okulmap-choro-legend" style={{position:'absolute',right:10,bottom:64,zIndex:9,background:isDark?'rgba(10,13,22,0.78)':'rgba(255,255,255,0.92)',backdropFilter:'blur(8px)',border:'1px solid '+(isDark?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.08)'),borderRadius:10,padding:'6px 9px',fontFamily:'DM Sans,sans-serif',fontSize:9,color:isDark?'rgba(255,255,255,0.7)':'#4B3D7A',pointerEvents:'none',boxShadow:'0 4px 12px rgba(0,0,0,0.2)'}}>
              <div style={{fontSize:8,letterSpacing:'0.06em',textTransform:'uppercase',color:isDark?'rgba(255,255,255,0.45)':'#9B8FBD',marginBottom:4}}>İl bazlı yoğunluk</div>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                {(isDark
                  ? [{c:'#2a2a3a',l:'0'},{c:'#E9D5FF',l:'1+'},{c:'#C4B5FD',l:'10+'},{c:'#A78BFA',l:'50+'},{c:'#7C3AED',l:'200+'}]
                  : [{c:'rgba(0,0,0,0.05)',l:'0'},{c:'#DDD6FE',l:'1+'},{c:'#A78BFA',l:'10+'},{c:'#7C3AED',l:'50+'},{c:'#5B21B6',l:'200+'}]
                ).map((x,i)=>(
                  <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                    <div style={{width:14,height:8,borderRadius:2,background:x.c,border:'1px solid '+(isDark?'rgba(255,255,255,0.15)':'rgba(0,0,0,0.12)')}}/>
                    <span>{x.l}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Map bottom category bar */}
            <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'10px 10px 10px',background:'linear-gradient(to top,rgba(10,13,22,0.88) 0%,transparent 100%)',zIndex:10,display:'flex',gap:6,overflowX:'auto',scrollbarWidth:'none',WebkitOverflowScrolling:'touch',pointerEvents:'none'}}>
              {CATS.map(c=>{
                const isAct = activeCategory===c.id;
                return (
                  <button key={c.id} onClick={()=>setActiveCategory(c.id)}
                    style={{display:'flex',alignItems:'center',gap:4,padding:'5px 11px',borderRadius:20,background:isAct?c.color:'rgba(10,13,22,0.7)',border:'1px solid '+(isAct?c.color:'rgba(255,255,255,0.12)'),color:isAct?'#000':'rgba(255,255,255,0.65)',fontSize:11,fontWeight:isAct?800:400,cursor:'pointer',whiteSpace:'nowrap',flexShrink:0,fontFamily:'DM Sans,sans-serif',transition:'all 0.15s',backdropFilter:'blur(8px)',pointerEvents:'all',boxShadow:isAct?'0 2px 8px '+c.color+'40':'none'}}>
                    {c.icon} {c.label}
                  </button>
                );
              })}
            </div>
            {compareList.length>0 && (
              <div style={{position:'absolute',bottom:60,right:20,background:C('nav'),backdropFilter:'blur(12px)',borderRadius:14,padding:'10px 14px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 32px rgba(0,0,0,0.4)',border:'1px solid rgba(168,218,255,0.3)',zIndex:10,animation:'slideInUp 0.3s ease',maxWidth:320}}>
                <div style={{fontSize:14,flexShrink:0}}>⚖️</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:'#fff',fontSize:11,fontWeight:700,marginBottom:2}}>{compareList.length}/3 okul seçildi</div>
                  <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                    {compareList.map(id=>{
                      const p = places.find(x=>x.id===id);
                      const col = p ? (TC[p.type]||'#A8DAFF') : '#A8DAFF';
                      return p ? (
                        <span key={id} style={{background:col+'18',border:'1px solid '+col+'35',borderRadius:6,padding:'1px 6px',fontSize:9,color:col,fontWeight:600,display:'flex',alignItems:'center',gap:3}}>
                          {p.name.length>12?p.name.slice(0,12)+'…':p.name}
                          <span onClick={e=>{e.stopPropagation();handleCompare(id);}} style={{cursor:'pointer',opacity:0.6,fontSize:10}}>×</span>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
                <button onClick={()=>setShowCompare(true)} style={{padding:'5px 12px',borderRadius:8,background:'rgba(168,218,255,0.2)',border:'1px solid rgba(168,218,255,0.4)',color:'#A8DAFF',fontSize:11,fontWeight:700,cursor:'pointer',flexShrink:0}}>Karşılaştır →</button>
                <button onClick={()=>setCompareList([])} style={{width:24,height:24,borderRadius:6,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.12)',color:'rgba(255,255,255,0.5)',cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>✕</button>
              </div>
            )}
          </div>

          {/* Mobile: list below map */}
          <div style={{display: isMobile && mobileTab==='map' ? 'block' : 'none'}}>
            {mode === 'safety'  && SafetyPanel()}
            <div style={{padding:'8px 12px',WebkitOverflowScrolling:'touch',minHeight:displayFiltered.length===0 ? 'calc(100vh - 200px)' : undefined}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                <span style={{color:C('txt2'),fontSize:12}}><span style={{color:C('txt'),fontWeight:700}}>{displayFiltered.length}</span> kurum</span>
                <div style={{display:'flex',gap:4}}>
                  {[{id:'distance',label:'Yakın'},{id:'rating',label:'Puan'},{id:'price',label:'Fiyat'}].filter(s=>s.id!=='distance'||userPos).map(s=>{
                    const isAct = sortBy===s.id;
                    return <button key={s.id} onClick={()=>setSortBy(s.id)} style={{padding:'3px 8px',borderRadius:5,fontSize:10,background:isAct?'rgba(168,218,255,0.14)':'rgba(255,255,255,0.04)',border:'1px solid '+(isAct?'rgba(168,218,255,0.4)':C('bord')),color:isAct?'#A8DAFF':C('txt3'),cursor:'pointer',fontWeight:isAct?700:400,fontFamily:'DM Sans,sans-serif'}}>{s.label}</button>;
                  })}
                </div>
              </div>
              {!loading && (
                <button onClick={()=>setShowPrefsModal(true)}
                  style={{width:'100%',padding:'10px 12px',marginBottom:8,borderRadius:10,background:userPrefs?'rgba(168,218,255,0.1)':'rgba(168,218,255,0.06)',border:'1px '+(userPrefs?'solid':'dashed')+' rgba(168,218,255,0.4)',color:'#A8DAFF',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                  🎯 {userPrefs?'Eşleştirme tercihimi düzenle':'Tercihini söyle, sana özel skor görelim'}
                </button>
              )}
              {loading ? [0,1,2,3].map(i=>(
                <div key={i} style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.05)',borderRadius:12,padding:'10px 12px',marginBottom:8,display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:42,height:42,borderRadius:10,flexShrink:0,background:'rgba(255,255,255,0.07)',animation:'skeletonPulse 1.4s ease-in-out infinite'}}/>
                  <div style={{flex:1}}><div style={{height:12,width:'55%',borderRadius:5,background:'rgba(255,255,255,0.07)',marginBottom:8,animation:'skeletonPulse 1.4s ease-in-out 0.1s infinite'}}/><div style={{height:9,width:'75%',borderRadius:4,background:'rgba(255,255,255,0.05)',animation:'skeletonPulse 1.4s ease-in-out 0.2s infinite'}}/></div>
                </div>
              )) : ListItems()}
            </div>
          </div>

          {/* Mobile: favorites */}
          <div style={{display: isMobile && mobileTab==='favs' ? 'flex' : 'none', flex:1, flexDirection:'column', overflowY:'auto', padding:16}}>
            {!user ? (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flex:1,gap:12}}>
                <div style={{fontSize:40}}>❤️</div>
                <div style={{color:C('txt'),fontSize:15,fontWeight:700}}>Favorilerim</div>
                <div style={{color:C('txt2'),fontSize:12,textAlign:'center'}}>Favori kurumlarını kaydetmek için giriş yapmalısın.</div>
                <button onClick={()=>setShowAuth(true)} style={{padding:'11px 28px',borderRadius:10,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',border:'none',color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Giriş Yap</button>
              </div>
            ) : favs.length===0 ? (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flex:1,gap:10,padding:'0 24px'}}>
                <Maskot type="mod" name="sad" size={88}/>
                <div style={{color:C('txt'),fontSize:15,fontWeight:700}}>Henüz favorin yok</div>
                <div style={{color:C('txt2'),fontSize:12,textAlign:'center',lineHeight:1.5}}>Beğendiğin okulları kaydetmek için detay sayfasındaki ❤️ butonuna bas. Buradan kolayca tekrar ulaşırsın.</div>
                <button onClick={()=>setMobileTab('map')} style={{marginTop:8,padding:'11px 24px',borderRadius:10,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',border:'none',color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                  🗺 Haritada Keşfet
                </button>
              </div>
            ) : filtered.filter(p=>favs.includes(p.id)).map(p=>{
              const col = TC[p.type]; const cat2 = CATS.find(c=>c.id===p.type)||{};
              return (
                <div key={p.id} className="card-hover" style={{background:'transparent',border:'1px solid '+C('bord'),borderRadius:12,padding:'11px 13px',marginBottom:8,cursor:'pointer',display:'flex',alignItems:'center',gap:10}} onClick={()=>setDetailId(p.id)}>
                  <div style={{width:42,height:42,borderRadius:10,flexShrink:0,background:col+'18',border:'1px solid '+col+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>{cat2.icon||''}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:C('txt'),fontSize:12,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                    <div style={{color:C('txt2'),fontSize:10,marginTop:2}}>★ {p.rating} · {p.age} yaş</div>
                  </div>
                  <span style={{color:p.priceNum>0?col:C('txt3'),fontSize:11,fontWeight:p.priceNum>0?700:500}}>{p.price}</span>
                </div>
              );
            })}
          </div>

          {/* Mobile: campaigns */}
          <div style={{display: isMobile && mobileTab==='events' ? 'flex' : 'none', flex:1, flexDirection:'column', overflowY:'auto'}}>
            {(()=>{
              const typeMap = {erken_kayit:{color:'#4ADE80',icon:'🎯'},acik_kapi:{color:'#A78BFA',icon:'📅'},indirim:{color:'#FB923C',icon:'💰'},etkinlik:{color:'#60A5FA',icon:'🎉'},duyuru:{color:'#F472B6',icon:'📢'},promo:{color:'#FFE66D',icon:'🎁'},event:{color:'#4ECDC4',icon:'🎉'},new:{color:'#FF6B8A',icon:'📢'}};
              return (
                <div style={{padding:'16px 16px 100px'}}>
                  <div style={{fontFamily:'Syne,sans-serif',fontSize:18,fontWeight:800,color:C('txt'),marginBottom:4}}>Kampanyalar</div>
                  <div style={{color:C('txt3'),fontSize:12,marginBottom:16}}>Kayıt dönemleri · Açık kapı günleri · İndirimler</div>
                  {campaigns.length===0 ? (
                    <div style={{textAlign:'center',padding:'40px 24px'}}>
                      <div style={{fontSize:48,marginBottom:12}}>🎯</div>
                      <div style={{color:C('txt'),fontSize:15,fontWeight:700,marginBottom:8}}>Şu an aktif kampanya yok</div>
                      <div style={{color:C('txt2'),fontSize:12,lineHeight:1.5}}>Yeni dönem kayıt indirimleri, açık kapı günleri ve etkinlikler burada görünecek.</div>
                    </div>
                  ) : campaigns.map(c=>{
                    const t = typeMap[c.type]||{color:'#A8DAFF',icon:'📌'};
                    const days = c.expires_at ? Math.ceil((new Date(c.expires_at).getTime()-Date.now())/86400000) : null;
                    const school = places.find(p=>p.id===c.school_id);
                    const schoolCat = CATS.find(cat=>cat.id===school?.type)||{icon:'📚',color:'#A8DAFF'};
                    return (
                      <div key={c.id}
                        style={{background:t.color+'08',border:'1px solid '+t.color+'50',borderRadius:12,marginBottom:10,cursor:'pointer'}}
                        className="card-hover"
                        onClick={()=>{ if(school) setDetailId(school.id); }}>
                        <div style={{padding:'12px 14px',display:'flex',alignItems:'flex-start',gap:10}}>
                          <div style={{width:36,height:36,borderRadius:9,background:schoolCat.color+'18',border:'1px solid '+schoolCat.color+'30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
                            {schoolCat.icon}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
                              <span style={{fontSize:12}}>{t.icon}</span>
                              <span style={{color:t.color,fontSize:13,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{c.title}</span>
                              {days!==null&&days>=0&&<span style={{fontSize:10,fontWeight:700,color:days<=7?'#FB923C':C('txt3'),flexShrink:0,background:days<=7?'rgba(251,146,60,0.12)':'rgba(255,255,255,0.06)',padding:'2px 6px',borderRadius:5}}>{days}g</span>}
                            </div>
                            {school&&<div style={{color:C('txt3'),fontSize:11,marginBottom:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>🏫 {school.name}</div>}
                            <div style={{color:C('txt2'),fontSize:12,lineHeight:1.4,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{c.body}</div>
                            {c.expires_at&&<div style={{color:C('txt3'),fontSize:10,marginTop:4}}>⏳ {new Date(c.expires_at).toLocaleDateString('tr-TR')}</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Mobile: profile */}
          <div style={{display: isMobile && mobileTab==='profile' ? 'flex' : 'none', flex:1, flexDirection:'column', overflowY:'auto', padding:24}}>
            {!user ? (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flex:1,gap:12}}>
                <div style={{fontSize:40}}>👤</div>
                <div style={{color:C('txt'),fontSize:15,fontWeight:700}}>Profilim</div>
                <div style={{color:C('txt2'),fontSize:12,textAlign:'center'}}>Hesabına giriş yaparak daha fazla özelliğe eriş.</div>
                <button onClick={()=>setShowAuth(true)} style={{padding:'11px 28px',borderRadius:10,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',border:'none',color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Giriş Yap</button>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  <div style={{width:60,height:60,borderRadius:18,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,fontWeight:800,color:'#000',overflow:'hidden'}}>
                    {(() => {
                      const role = user?.user_metadata?.role || (typeof window!=='undefined' && localStorage.getItem('okulmap_user_role')) || null;
                      return role ? <Maskot type="rol" name={role} size={56}/> : (user.name||user.email||'?')[0].toUpperCase();
                    })()}
                  </div>
                  <div>
                    <div style={{color:C('txt'),fontSize:16,fontWeight:800}}>{user.name||'Kullanıcı'}</div>
                    <div style={{color:C('txt2'),fontSize:12}}>{user.email}</div>
                  </div>
                </div>
                <button onClick={()=>setShowPrefsModal(true)} style={{padding:'12px 14px',borderRadius:10,background:userPrefs?'rgba(168,218,255,0.12)':'rgba(168,218,255,0.06)',border:'1px '+(userPrefs?'solid':'dashed')+' rgba(168,218,255,0.4)',color:'#A8DAFF',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginTop:8,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                  🎯 {userPrefs?'Eşleştirme tercihimi düzenle':'Sana özel uygunluk skoru — tercihini söyle'}
                </button>
                {staffInfo && (
                  <a href={staffInfo.url}
                    style={{padding:'12px 14px',borderRadius:10,background:'#1E1048',color:'#fff',fontSize:13,fontWeight:700,textDecoration:'none',fontFamily:'DM Sans,sans-serif',marginTop:4,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                    🏫 Yönetim Paneline Git →
                  </a>
                )}
                <button onClick={async()=>{await supabase.auth.signOut();localStorage.setItem('okulmap_signed_out','1');setUser(null);}} style={{padding:'11px',borderRadius:10,background:'rgba(255,107,107,0.12)',border:'1px solid rgba(255,107,107,0.3)',color:'#FF6B6B',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginTop:4}}>
                  Çıkış Yap
                </button>
              </div>
            )}
            {!user && (
              <button onClick={()=>setShowPrefsModal(true)} style={{padding:'12px 14px',borderRadius:10,background:userPrefs?'rgba(168,218,255,0.12)':'rgba(168,218,255,0.06)',border:'1px '+(userPrefs?'solid':'dashed')+' rgba(168,218,255,0.4)',color:'#A8DAFF',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginTop:18,display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%'}}>
                🎯 {userPrefs?'Eşleştirme tercihimi düzenle':'Sana özel uygunluk skoru — tercihini söyle'}
              </button>
            )}
          </div>

        </div>

          {/* Mobile: compare tab */}
          <div style={{display: isMobile && mobileTab==='compare' ? 'flex' : 'none', flex:1, flexDirection:'column', overflowY:'auto', padding:'16px 16px 100px'}}>
            {(()=>{
              const cmpItems = compareList.map(id=>places.find(p=>p.id===id)).filter(Boolean);
              const ROWS = [
                {label:'Puan',   fn:(p)=><><span style={{color:'#FFE66D'}}>★</span> {p.rating}</>},
                {label:'Fiyat',  fn:(p)=>p.price},
                {label:'Yaş',    fn:(p)=>p.age+' yaş'},
                {label:'Durum',  fn:(p)=>p.open?<span style={{color:'#4ECDC4'}}>Açık</span>:<span style={{color:'#888'}}>Kapalı</span>},
                {label:'OkulBot',fn:(p)=>p.okulbot?<span style={{color:'#A8DAFF'}}>✓</span>:<span style={{color:'rgba(255,255,255,0.2)'}}>—</span>},
              ];
              return (
                <>
                  <div style={{fontFamily:'Syne,sans-serif',fontSize:18,fontWeight:800,color:C('txt'),marginBottom:4}}>Karşılaştır</div>
                  <div style={{color:C('txt3'),fontSize:12,marginBottom:16}}>Okul detaylarından "+ Karşılaştır" butonuna bas</div>
                  {cmpItems.length===0 ? (
                    <div style={{textAlign:'center',padding:'40px 24px',color:C('txt2')}}>
                      <div style={{fontSize:48,marginBottom:12}}>⚖️</div>
                      <div style={{color:C('txt'),fontSize:15,fontWeight:700,marginBottom:8}}>Karşılaştırma için okul seç</div>
                      <div style={{color:C('txt2'),fontSize:12,lineHeight:1.5,marginBottom:14}}>3 okula kadar ekleyebilirsin. Detay sayfasındaki <span style={{color:'#A8DAFF',fontWeight:700}}>terazi</span> ikonuna basarak ekle.</div>
                      <button onClick={()=>setMobileTab('map')} style={{padding:'11px 24px',borderRadius:10,background:'linear-gradient(135deg,#A8DAFF,#7BB8F0)',border:'none',color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                        🗺 Okul Bul
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{display:'flex',gap:8,marginBottom:16,overflowX:'auto'}}>
                        {cmpItems.map(p=>{ const col=TC[p.type]; const cat=CATS.find(c=>c.id===p.type)||{}; return (
                          <div key={p.id} style={{flex:'0 0 calc(50% - 4px)',background:col+'12',border:'1px solid '+col+'40',borderRadius:12,padding:12,position:'relative',textAlign:'center'}}>
                            <button onClick={()=>setCompareList(prev=>prev.filter(x=>x!==p.id))}
                              style={{position:'absolute',top:6,right:6,width:18,height:18,borderRadius:'50%',background:'rgba(255,255,255,0.1)',border:'none',color:C('txt2'),fontSize:9,cursor:'pointer'}}>✕</button>
                            <div style={{fontSize:22,marginBottom:4}}>{cat.icon}</div>
                            <div style={{color:C('txt'),fontSize:12,fontWeight:700,lineHeight:1.3}}>{p.name}</div>
                            <div style={{color:col,fontSize:11,marginTop:3}}>★ {p.rating}</div>
                          </div>
                        );})}
                      </div>
                      <div style={{background:'rgba(255,255,255,0.02)',borderRadius:14,border:'1px solid '+C('bord'),overflow:'hidden',marginBottom:16}}>
                        {ROWS.map((row,ri)=>(
                          <div key={ri} style={{display:'flex',borderBottom:ri<ROWS.length-1?'1px solid '+C('bord'):'none'}}>
                            <div style={{width:72,padding:'10px 10px',fontSize:11,color:C('txt2'),fontWeight:600,flexShrink:0,borderRight:'1px solid '+C('bord'),display:'flex',alignItems:'center'}}>{row.label}</div>
                            {cmpItems.map((p,pi)=>(
                              <div key={pi} style={{flex:1,padding:'10px 10px',fontSize:12,color:C('txt'),borderRight:pi<cmpItems.length-1?'1px solid '+C('bord'):'none',display:'flex',alignItems:'center'}}>{row.fn(p)}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                      {cmpItems.length>=2 && (()=>{
                        const maxP = Math.max(...cmpItems.map(p=>p.priceNum));
                        if (!maxP) return null;
                        return (
                          <div style={{marginBottom:16}}>
                            <div style={{color:C('txt3'),fontSize:11,fontWeight:700,letterSpacing:'0.08em',marginBottom:8}}>FİYAT KARŞILAŞTIRMASI</div>
                            {cmpItems.map(p=>{ const pct=p.priceNum>0?(p.priceNum/maxP)*100:0; const col=TC[p.type]||'#A8DAFF'; return (
                              <div key={p.id} style={{marginBottom:10}}>
                                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                                  <span style={{color:C('txt2'),fontSize:11,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'65%'}}>{p.name}</span>
                                  <span style={{color:p.priceNum>0?col:C('txt3'),fontSize:11,fontWeight:p.priceNum>0?700:500}}>{p.price}</span>
                                </div>
<div style={{height:5,background:'rgba(255,255,255,0.06)',borderRadius:3,overflow:'hidden'}}>
                                  <div style={{height:'100%',width:pct+'%',background:col,borderRadius:3}}/>
                                </div>
                              </div>
                            );})}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </>
              );
            })()}
          </div>

        {/* Right panel — desktop only */}
        {!isMobile && RightPanel()}
        </div>
      </div>

      {/* Mobile bottom nav */}
      {isMobile && <MobileBottomNav theme={theme} favs={favs} compareList={compareList} mobileTab={mobileTab} setMobileTab={setMobileTab} mapRef={mapRef} />}

      {/* Mobile sheets */}
      {isMobile && <MobileFilterSheet
        theme={theme}
        showMobileFilter={showMobileFilter} setShowMobileFilter={setShowMobileFilter}
        userPos={userPos} sortBy={sortBy} setSortBy={setSortBy}
        selectedAgeGroup={selectedAgeGroup} setSelectedAgeGroup={setSelectedAgeGroup}
        priceRange={priceRange} setPriceRange={setPriceRange} displayFiltered={displayFiltered}
      />}
      {isMobile && <MobileLocationSheet
        theme={theme}
        showMobileLocation={showMobileLocation} setShowMobileLocation={setShowMobileLocation}
        selectedIl={selectedIl} setSelectedIl={setSelectedIl}
        selectedIlce={selectedIlce} setSelectedIlce={setSelectedIlce}
        ilQuery={ilQuery} setIlQuery={setIlQuery}
      />}

      {/* Etkinlik Başvuru Modalı */}
      {eventReserveModal && (()=>{
        const ev = eventReserveModal;
        const EVT_ICONS = {at_binicilik:'🐴',yuzme:'🏊',bale:'🩰',satranc:'♟️',resim:'🎨',doga:'🌿',spor:'⚽',muzik:'🎵',diger:'📅'};
        const EVT_COLORS = {at_binicilik:'#8B4513',yuzme:'#0EA5E9',bale:'#EC4899',satranc:'#6366F1',resim:'#F59E0B',doga:'#22C55E',spor:'#EF4444',muzik:'#A855F7',diger:'#FF8C42'};
        const icon = EVT_ICONS[ev.type]||'📅';
        const color = EVT_COLORS[ev.type]||'#FF8C42';
        const dateStr = new Date(ev.date).toLocaleDateString('tr-TR',{weekday:'long',day:'numeric',month:'long'});
        return (
          <div style={{position:'fixed',inset:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.7)',backdropFilter:'blur(4px)'}}
            onClick={()=>setEventReserveModal(null)}>
            <div style={{background:C('bg'),border:'1px solid '+C('bord'),borderRadius:20,width:'100%',maxWidth:400,overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.5)',animation:'slideUp 0.25s ease'}}
              onClick={e=>e.stopPropagation()}>
              {/* Header */}
              <div style={{background:`linear-gradient(135deg,${color}ee,${color}88)`,padding:'20px 24px',position:'relative'}}>
                <div style={{fontSize:36,marginBottom:6}}>{icon}</div>
                <div style={{fontSize:18,fontWeight:800,color:'#fff'}}>{ev.title}</div>
                <div style={{fontSize:12,color:'rgba(255,255,255,0.7)',marginTop:4}}>📅 {dateStr}{ev.time_start?' · 🕐 '+ev.time_start:''}</div>
                <button onClick={()=>setEventReserveModal(null)} style={{position:'absolute',top:12,right:14,background:'rgba(0,0,0,0.3)',border:'none',color:'#fff',width:28,height:28,borderRadius:'50%',fontSize:14,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
              </div>
              {/* Form */}
              <div style={{padding:'20px 24px'}}>
                {reserveMsg === 'success' ? (
                  <div style={{textAlign:'center',padding:'20px 0'}}>
                    <div style={{fontSize:48,marginBottom:12}}>🎉</div>
                    <div style={{fontSize:16,fontWeight:700,color:C('txt'),marginBottom:6}}>Başvurunuz Alındı!</div>
                    <div style={{fontSize:13,color:C('txt3'),lineHeight:1.5}}>Okul en kısa sürede sizinle iletişime geçecektir.</div>
                    <button onClick={()=>setEventReserveModal(null)} style={{marginTop:16,padding:'10px 32px',borderRadius:12,background:color,border:'none',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Tamam</button>
                  </div>
                ) : (
                  <>
                    {reserveMsg && <div style={{padding:'8px 12px',borderRadius:10,background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',color:'#EF4444',fontSize:12,marginBottom:12}}>{reserveMsg}</div>}
                    <div style={{display:'flex',flexDirection:'column',gap:12}}>
                      <div>
                        <label style={{display:'block',fontSize:11,fontWeight:600,color:C('txt3'),marginBottom:4}}>Ad Soyad *</label>
                        <input value={reserveForm.name} onChange={e=>setReserveForm(f=>({...f,name:e.target.value}))} placeholder="Adınız Soyadınız"
                          style={{width:'100%',padding:'10px 14px',borderRadius:10,background:C('inp'),border:'1px solid '+C('bord'),color:C('txt'),fontSize:14,fontFamily:'DM Sans,sans-serif',outline:'none'}} />
                      </div>
                      <div>
                        <label style={{display:'block',fontSize:11,fontWeight:600,color:C('txt3'),marginBottom:4}}>Telefon *</label>
                        <input value={reserveForm.phone} onChange={e=>setReserveForm(f=>({...f,phone:formatPhone(e.target.value)}))} placeholder="+90 (532) 000 00 00" type="tel"
                          style={{width:'100%',padding:'10px 14px',borderRadius:10,background:C('inp'),border:'1px solid '+C('bord'),color:C('txt'),fontSize:14,fontFamily:'DM Sans,sans-serif',outline:'none'}} />
                      </div>
                      <div>
                        <label style={{display:'block',fontSize:11,fontWeight:600,color:C('txt3'),marginBottom:4}}>Kişi Sayısı</label>
                        <input type="number" value={reserveForm.guest_count} onChange={e=>setReserveForm(f=>({...f,guest_count:e.target.value}))} min="1"
                          style={{width:'100%',padding:'10px 14px',borderRadius:10,background:C('inp'),border:'1px solid '+C('bord'),color:C('txt'),fontSize:14,fontFamily:'DM Sans,sans-serif',outline:'none'}} />
                      </div>
                      <div>
                        <label style={{display:'block',fontSize:11,fontWeight:600,color:C('txt3'),marginBottom:4}}>Not (opsiyonel)</label>
                        <textarea value={reserveForm.note} onChange={e=>setReserveForm(f=>({...f,note:e.target.value}))} rows={2} placeholder="Eklemek istediğiniz bir not..."
                          style={{width:'100%',padding:'10px 14px',borderRadius:10,background:C('inp'),border:'1px solid '+C('bord'),color:C('txt'),fontSize:14,fontFamily:'DM Sans,sans-serif',outline:'none',resize:'none'}} />
                      </div>
                    </div>
                    <button onClick={submitReservation} disabled={reserveSaving}
                      style={{marginTop:16,width:'100%',padding:'13px 0',borderRadius:12,background:`linear-gradient(135deg,${color},${color}cc)`,border:'none',color:'#fff',fontSize:15,fontWeight:800,cursor:'pointer',fontFamily:'DM Sans,sans-serif',opacity:reserveSaving?0.6:1}}>
                      {reserveSaving ? 'Gönderiliyor...' : '🎟 Başvuruyu Gönder'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
